import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import StatCards from '../../components/StatCards';
import DeleteConfirmModal from '../../components/DeleteConfirmModal';
import DeleteSuccessModal from '../../components/DeleteSuccessModal';
import { getAllOrders, updateOrderStatus, deleteOrder, getAllCustomers, getCustomerById, getProductById,
  updateOrderShippingAddress, createShipment, assignAWB, getShippingLabel, getShippingInvoice, getCourierOptions, IMAGE_BASE_URL } from '../../services/api';
import ExcelJS from 'exceljs';

const ITEMS_PER_PAGE = 10;

/* Helpers */
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const fmtINR = (n) => 'Rs. ' + (Number(n) || 0).toLocaleString('en-IN');

const getEntityName = (entity) => {
  if (!entity || typeof entity !== 'object') return '';
  if (entity.name) return entity.name;
  if (entity.fullName) return entity.fullName;
  if (entity.customerName) return entity.customerName;
  if (entity.phone) return entity.phone;
  const first = entity.firstName || entity.firstname || entity.first;
  const last = entity.lastName || entity.lastname || entity.last;
  if (first) return first + (last ? ' ' + last : '');
  return '';
};

const getCustomerName = (order) => {
  if (!order) return 'Guest';
  const possible = [order.user, order.userId, order.customer, order.customerId];
  for (const entity of possible) {
    const name = getEntityName(entity);
    if (name) return name;
  }
  if (order.customerName) return order.customerName;
  return 'Guest';
};

const getCustomerEmail = (order) => {
  if (!order) return '';
  if (order.user && typeof order.user === 'object')         return order.user.email || '';
  if (order.userId && typeof order.userId === 'object')     return order.userId.email || '';
  if (order.customer && typeof order.customer === 'object') return order.customer.email || '';
  if (order.customerId && typeof order.customerId === 'object') return order.customerId.email || '';
  return order.customerEmail || '';
};

const getCustomerPhone = (order) => {
  if (!order) return '';
  if (order.user && typeof order.user === 'object')     return order.user.phone || '';
  if (order.userId && typeof order.userId === 'object') return order.userId.phone || '';
  if (order.customer && typeof order.customer === 'object') return order.customer.phone || '';
  if (order.customerId && typeof order.customerId === 'object') return order.customerId.phone || '';
  return order.customerPhone || order.phone || order.mobile || order.contact || '';
};

const getApiErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  const readable = value => {
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (!value || typeof value !== 'object') return '';
    return value.label || value.field || value.key || value.name || value.message || '';
  };
  if (!data) return fallback;
  if (typeof data === 'string') {
    return /<!doctype|<html|Cannot\s+(GET|POST|PUT|PATCH|DELETE)/i.test(data) ? fallback : data;
  }
  if (data.error) return data.error;
  if (data.message) return data.message;
  if (data.details) {
    if (typeof data.details === 'string') return data.details;
    if (Array.isArray(data.details)) return data.details.map(readable).filter(Boolean).join(', ') || fallback;
  }
  if (data.errors && typeof data.errors === 'object') {
    return Object.values(data.errors).flat().map(readable).filter(Boolean).join(', ') || fallback;
  }
  return fallback;
};

const INDIAN_PINCODE_RE = /^[1-9][0-9]{5}$/;

const firstString = (values) => {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const str = String(value).trim();
    if (str) return str;
  }
  return '';
};

const normalizeKeyPart = (value) => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    const nested = value._id ?? value.id ?? value.$oid ?? value.oid ?? value.sku ?? value.slug ?? value.code;
    return nested === value ? '' : normalizeKeyPart(nested);
  }
  return '';
};

const makeSafeKey = (prefix, entity, index) => {
  const id = normalizeKeyPart(entity?._id) || normalizeKeyPart(entity?.id) || normalizeKeyPart(entity?.productId) || normalizeKeyPart(entity?.product) || normalizeKeyPart(entity?.courierId) || normalizeKeyPart(entity?.sku) || normalizeKeyPart(entity?.code);
  return `${prefix}-${id || 'row'}-${index}`;
};

const getNestedAddress = (order) => {
  const candidates = [order?.shippingAddress, order?.deliveryAddress, order?.address, order?.billingAddress];
  return candidates.find(value => value && typeof value === 'object' && !Array.isArray(value)) || {};
};

const getRawAddressText = (order) => {
  const candidates = [order?.shippingAddress, order?.deliveryAddress, order?.address, order?.billingAddress];
  const direct = candidates.find(value => typeof value === 'string' && value.trim());
  return direct ? direct.trim() : '';
};

const getShippingAddress = (order) => {
  const addressObj = getNestedAddress(order);
  const rawAddress = getRawAddressText(order);
  const line1 = firstString([
    addressObj.address,
    addressObj.addressLine1,
    addressObj.line1,
    addressObj.street,
    addressObj.street1,
    order?.shippingAddressLine1,
    order?.addressLine1,
    order?.address1,
    rawAddress,
  ]);
  const line2 = firstString([
    addressObj.address2,
    addressObj.addressLine2,
    addressObj.line2,
    addressObj.street2,
    addressObj.landmark,
    order?.shippingAddressLine2,
    order?.addressLine2,
    order?.address2,
    order?.landmark,
  ]);
  const pincode = firstString([
    addressObj.pincode,
    addressObj.pinCode,
    addressObj.postalCode,
    addressObj.postcode,
    addressObj.zip,
    order?.pincode,
    order?.pinCode,
    order?.postalCode,
    order?.postcode,
    order?.zip,
    order?.shippingPincode,
    order?.shippingPinCode,
    order?.shippingPostalCode,
  ]);

  return {
    name: firstString([addressObj.name, addressObj.fullName, addressObj.customerName, order?.shippingName, order?.customerName, getCustomerName(order)]),
    phone: firstString([addressObj.phone, addressObj.mobile, addressObj.contact, order?.shippingPhone, order?.customerPhone, order?.phone, order?.mobile, getCustomerPhone(order)]),
    email: firstString([addressObj.email, order?.shippingEmail, order?.customerEmail, getCustomerEmail(order)]),
    address: line1,
    address2: line2,
    city: firstString([addressObj.city, addressObj.town, order?.shippingCity, order?.city]),
    state: firstString([addressObj.state, addressObj.province, addressObj.region, order?.shippingState, order?.state]),
    pincode,
    country: firstString([addressObj.country, order?.shippingCountry, order?.country, 'India']),
    rawAddress,
  };
};


const SHIPPING_REQUIRED_FIELDS = [
  { key: 'name', label: 'Full Name' },
  { key: 'phone', label: 'Phone Number' },
  { key: 'address', label: 'Address Line 1' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'pincode', label: 'Pincode' },
  { key: 'country', label: 'Country' },
];

const getShippingAddressMissingFields = (order) => {
  const address = getShippingAddress(order);
  return SHIPPING_REQUIRED_FIELDS.filter(field => {
    if (field.key === 'pincode') return !INDIAN_PINCODE_RE.test(address.pincode || '');
    return !String(address[field.key] || '').trim();
  });
};


const getOrderStatus = (order) => {
  if (!order) return '';
  return String(order.orderStatus || order.status || order.order_status || '').toLowerCase().trim();
};

const getOrderTotal = (order) => {
  if (!order) return 0;
  return Number(order.totalAmount || order.total || order.amount || order.orderTotal || order.grandTotal || 0);
};

const resolveOrderCustomerId = (order) => {
  if (!order) return null;
  if (order.user && typeof order.user === 'object')         return order.user._id || order.user.id || null;
  if (order.userId && typeof order.userId === 'object')     return order.userId._id || order.userId.id || null;
  if (order.customer && typeof order.customer === 'object') return order.customer._id || order.customer.id || null;
  if (typeof order.user     === 'string') return order.user;
  if (typeof order.userId   === 'string') return order.userId;
  if (typeof order.customer === 'string') return order.customer;
  if (typeof order.customerId === 'string') return order.customerId;
  return null;
};

const fetchCustomersByIds = async (ids) => {
  if (!ids || !ids.length) return {};
  const results = await Promise.allSettled(ids.map(id => getCustomerById(id)));
  return results.reduce((acc, res, idx) => {
    if (res.status === 'fulfilled') {
      const customer = res.value?.data?.data || res.value?.data?.customer || res.value?.data;
      if (customer) {
        const id = customer._id || customer.id || ids[idx];
        if (id) acc[id] = customer;
      }
    }
    return acc;
  }, {});
};

const getOrderItemList = (order) => {
  return order.products || order.items || order.orderItems || order.order_items || [];
};

const getOrderCouponCode = (order) => {
  if (!order) return '';
  const coupon = order.coupon || order.appliedCoupon || order.couponDetails;
  if (typeof coupon === 'string') return coupon;
  return order.couponCode || order.coupon_code || coupon?.couponCode || coupon?.code || '';
};

const getOrderCouponDiscount = (order) => {
  if (!order) return 0;
  const coupon = order.coupon || order.appliedCoupon || order.couponDetails;
  const directDiscount = order.couponDiscount ?? order.couponDiscountAmount ?? order.discountAmount;
  const nestedDiscount = coupon && typeof coupon === 'object'
    ? (coupon.discountAmount ?? coupon.couponDiscount ?? coupon.amount)
    : undefined;
  return Number(directDiscount ?? nestedDiscount ?? 0) || 0;
};

const getOrderSubtotal = (order) => getOrderItemList(order).reduce((subtotal, item) => {
  const product = getRelatedProduct(item);
  const price = Number(product.price ?? item.price ?? 0) || 0;
  const quantity = Number(item.quantity ?? item.qty ?? 1) || 1;
  return subtotal + (price * quantity);
}, 0);

const extractProductId = (item) => {
  if (!item) return null;
  if (item.product && typeof item.product === 'object') return item.product._id || item.product.id || null;
  if (item.productId && typeof item.productId === 'object') return item.productId._id || item.productId.id || null;
  if (typeof item.product === 'string') return item.product;
  if (typeof item.productId === 'string') return item.productId;
  return null;
};

const getRelatedProduct = (item) => {
  if (!item) return {};
  if (item.product && typeof item.product === 'object') return item.product;
  if (item.productId && typeof item.productId === 'object') return item.productId;
  return item.productDetails || item.product_info || item.product_data || {};
};

const fetchProductsByIds = async (ids) => {
  if (!ids || !ids.length) return {};
  const results = await Promise.allSettled(ids.map(id => getProductById(id)));
  return results.reduce((acc, res, idx) => {
    if (res.status === 'fulfilled') {
      const product = res.value?.data?.data || res.value?.data?.product || res.value?.data;
      if (product) {
        const id = product._id || product.id || ids[idx];
        if (id) acc[id] = product;
      }
    }
    return acc;
  }, {});
};

const enrichOrdersWithProducts = async (orders) => {
  const missingProductIds = Array.from(new Set(
    orders.flatMap(order => getOrderItemList(order).map(extractProductId).filter(id => typeof id === 'string'))
  ));
  if (!missingProductIds.length) return orders;
  const productMap = await fetchProductsByIds(missingProductIds);
  return orders.map((order) => {
    const items = getOrderItemList(order).map((item) => {
      const productId = extractProductId(item);
      const product = productId ? productMap[productId] : null;
      if (!product) return item;
      return {
        ...item,
        product: typeof item.product === 'string' ? product : item.product,
        productId: typeof item.productId === 'string' ? product : item.productId,
      };
    });
    return {
      ...order,
      products: order.products ? items : order.products,
      items: order.items ? items : order.items,
      orderItems: order.orderItems ? items : order.orderItems,
      order_items: order.order_items ? items : order.order_items,
    };
  });
};

const isOrderActive = (order) => !order?.isDeleted;

const getFirstProduct = (order) => {
  const items = getOrderItemList(order);
  if (items.length === 0) return null;
  const item = items[0];
  const prod = getRelatedProduct(item);
  return {
    name:  prod.name || item.name || item.productName || 'Product',
    count: items.length,
  };
};

const getItemImage = (item) => {
  const prod = getRelatedProduct(item);
  const image = prod.image || prod.imageUrl || prod.image_url || prod.imageURL || prod.imagePath || prod.photo || prod.picture || (prod.images && prod.images[0]) || (prod.photos && prod.photos[0]) || prod.thumbnail || item.image || item.imageUrl || item.image_url || item.imageURL || (item.images && item.images[0]) || item.thumbnail || item.productImage || '';
  return resolveOrderImageUrl(image);
};

const resolveOrderImageUrl = (value) => {
  if (!value) return '';
  if (typeof value === 'string' && (value.trim().startsWith('[') || value.trim().startsWith('{'))) {
    try {
      const parsed = JSON.parse(value);
      return resolveOrderImageUrl(Array.isArray(parsed) ? parsed[0] : parsed);
    } catch (e) { return ''; }
  }
  if (Array.isArray(value)) return resolveOrderImageUrl(value[0]);
  if (value && typeof value === 'object') {
    return resolveOrderImageUrl(value.url || value.src || value.path || value.image || value.imageUrl || value.filename || value.secure_url || value.location);
  }
  if (typeof value !== 'string' || !value.trim()) return '';
  const imagePath = value.trim().replace(/\\/g, '/');
  if (imagePath.startsWith('blob:') || imagePath.startsWith('data:')) return imagePath;
  const base = String(IMAGE_BASE_URL || 'https://api.ishmaijewellery.com').replace(/\/+$/, '');
  if (/^https?:\/\//i.test(imagePath)) {
    const uploadIndex = imagePath.indexOf('/uploads/');
    return uploadIndex === -1 ? imagePath : base + imagePath.substring(uploadIndex);
  }
  const cleanPath = imagePath.replace(/^\/+/, '').replace(/^api\//, '');
  return base + '/' + cleanPath;
};

const getOrderField = (order, keys) => {
  for (const key of keys) {
    if (order?.[key] !== undefined && order?.[key] !== null && String(order[key]).trim() !== '') return order[key];
  }
  return undefined;
};

const getPaymentStatus = (order) => {
  if (!order || typeof order !== 'object') return '';
  const payment = order.payment && typeof order.payment === 'object' ? order.payment : {};
  const refund = order.refund && typeof order.refund === 'object' ? order.refund : {};
  const refundStatus = getOrderField(order, ['refundStatus', 'refund_status'])
    ?? getOrderField(refund, ['status', 'refundStatus', 'refund_status'])
    ?? getOrderField(payment, ['refundStatus', 'refund_status']);
  return String(refundStatus ?? getOrderField(order, ['paymentStatus', 'payment_status'])
    ?? getOrderField(payment, ['status', 'paymentStatus', 'payment_status'])
    ?? order.payment_state ?? '').trim();
};

const getRefundDetails = (order) => {
  if (!order || typeof order !== 'object') return {};
  const refund = order.refund && typeof order.refund === 'object' ? order.refund : {};
  const payment = order.payment && typeof order.payment === 'object' ? order.payment : {};
  return {
    status: getOrderField(order, ['refundStatus', 'refund_status'])
      ?? getOrderField(refund, ['status', 'refundStatus', 'refund_status'])
      ?? getOrderField(payment, ['refundStatus', 'refund_status']),
    id: getOrderField(order, ['refundId', 'refundID', 'refund_id'])
      ?? getOrderField(refund, ['id', '_id', 'refundId', 'refundID', 'refund_id']),
    amount: getOrderField(order, ['refundAmount', 'refund_amount'])
      ?? getOrderField(refund, ['amount', 'refundAmount', 'refund_amount']),
    date: getOrderField(order, ['refundedAt', 'refundDate', 'refund_date'])
      ?? getOrderField(refund, ['refundedAt', 'refundDate', 'refund_date', 'date']),
  };
};

const getPaymentStatusClass = (status) => {
  const key = String(status || '').toLowerCase().trim();
  if (['paid', 'success', 'successful', 'completed', 'captured', 'refunded', 'refund_completed', 'refund_successful'].includes(key)) return 'bdg-g';
  if (['failed', 'cancelled', 'canceled', 'rejected'].includes(key)) return 'bdg-r';
  if (['pending', 'processing', 'refund_pending'].includes(key)) return 'bdg-y';
  return 'bdg-b';
};

const getPaymentId = (order) => getOrderField(order, ['transactionId', 'transactionID', 'paymentId', 'paymentID', 'razorpayPaymentId', 'razorpay_payment_id']);

const getSuperCoinDetails = (order) => {
  const nested = order?.superCoins && typeof order.superCoins === 'object' ? order.superCoins : {};
  const used = getOrderField(order, ['superCoinsUsed', 'superCoinUsed', 'coinsUsed']) ?? getOrderField(nested, ['used', 'coins', 'count']);
  const amount = getOrderField(order, ['superCoinsDiscount', 'superCoinsAmount', 'superCoinDiscount', 'superCoinAmount', 'coinDiscount']) ?? getOrderField(nested, ['discount', 'discountAmount', 'amount', 'value']);
  return { used, amount, hasValue: used !== undefined || amount !== undefined };
};

const getGiftPackingDetails = (order) => {
  const nested = order?.giftPacking && typeof order.giftPacking === 'object' ? order.giftPacking : {};
  const direct = getOrderField(order, ['isGiftPacking', 'giftWrap', 'isGiftWrapped']);
  const enabled = direct ?? getOrderField(nested, ['enabled', 'isEnabled', 'selected']) ?? (order?.giftPacking && typeof order.giftPacking === 'object' ? true : undefined);
  const amount = getOrderField(order, ['giftPackingAmount', 'giftPackAmount', 'giftWrapAmount']) ?? getOrderField(nested, ['amount', 'price', 'cost']);
  const hasValue = enabled !== undefined || amount !== undefined || Object.keys(nested).length > 0;
  return { enabled: Boolean(enabled === true || enabled === 'true' || enabled === 'yes' || enabled === 'Yes'), amount, hasValue };
};

const getItemVariant = (item) => {
  const product = getRelatedProduct(item);
  return getOrderField(item, ['variant', 'variantName', 'size']) || getOrderField(product, ['variant', 'variantName', 'size']);
};

const getItemDescription = (item) => {
  const prod = getRelatedProduct(item);
  return prod.description || prod.shortDescription || prod.productDescription || item.description || item.shortDescription || item.productDescription || '';
};

const statusMeta = {
  pending:   { cls: 'bdg-b', label: 'Pending'   },
  confirmed: { cls: 'bdg-y', label: 'Confirmed' },
  shipped:   { cls: 'bdg-o', label: 'Shipped'   },
  delivered: { cls: 'bdg-g', label: 'Delivered' },
  cancelled: { cls: 'bdg-r', label: 'Cancelled' },
  rejected:  { cls: 'bdg-r', label: 'Rejected'  },
};
const getStatus = (s, cancelledBy) => {
  const key = (s || '').toLowerCase();
  if (key === 'cancelled' && (cancelledBy || '').toLowerCase() === 'admin') {
    return statusMeta.rejected;
  }
  return statusMeta[key] || { cls: 'bdg-b', label: s || 'Pending' };
};
const normalizeStatus = (s) => (s || '').toLowerCase();

/* Forward-only status transition maps
   Each key lists the statuses that are valid choices FROM that
   current status (always includes itself so the current value
   still renders in the <select>). Anything not listed is a
   backward move and must not be offered. */
const ORDER_STATUS_TRANSITIONS = {
  pending:   ['pending', 'confirmed', 'cancelled'],
  confirmed: ['confirmed', 'shipped', 'cancelled'],
  shipped:   ['shipped', 'delivered'],   // no cancel once shipped
  delivered: ['delivered'],              // locked, final state
  cancelled: ['cancelled'],              // locked, final state
};

const PAYMENT_STATUS_TRANSITIONS = {
  pending:   ['pending', 'paid', 'failed'],
  paid:      ['paid'],                   // locked, no reverting to pending
  failed:    ['failed', 'pending'],      // allow retry
  refunded:  ['refunded'],              // locked, final payment state
  cancelled: ['cancelled'],              // locked, final state
};

const getOrderStatusOptions = (current) =>
  ORDER_STATUS_TRANSITIONS[current] || ['pending', 'confirmed', 'shipped', 'delivered', 'cancelled'];

const getPaymentStatusOptions = (current) =>
  PAYMENT_STATUS_TRANSITIONS[current] || ['pending', 'paid', 'failed'];

const isOrderStatusLocked = (current) => (ORDER_STATUS_TRANSITIONS[current] || []).length <= 1;
const isPaymentStatusLocked = (current) => (PAYMENT_STATUS_TRANSITIONS[current] || []).length <= 1;

const ORDER_STATUS_LABELS = {
  pending: 'Pending', confirmed: 'Confirmed', shipped: 'Shipped', delivered: 'Delivered', cancelled: 'Rejected',
};
const PAYMENT_STATUS_LABELS = {
  pending: 'Pending', paid: 'Paid', failed: 'Failed', refunded: 'Refunded', cancelled: 'Rejected',
};

const pgBtn = (disabled, active) => ({
  minWidth: 34, height: 34, padding: '0 10px', borderRadius: 8,
  border: active ? '1px solid #ff4da6' : '1px solid rgba(255,255,255,0.12)',
  background: active ? 'rgba(255,77,166,0.2)' : disabled ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.08)',
  color: active ? '#ff4da6' : disabled ? '#64748b' : '#e2e8f0',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: 13, fontWeight: active ? 700 : 500,
  fontFamily: "'Sora','Outfit',sans-serif", transition: 'all .15s',
  opacity: disabled ? 0.5 : 1,
});

const Pagination = ({ currentPage, totalPages, onPageChange, totalItems }) => {
  if (totalPages <= 1) return null;
  const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endItem   = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);
  const getPageNumbers = () => {
    const pages = [], maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end   = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 4px 4px', borderTop:'1px solid rgba(14,165,233,0.12)', marginTop:8, flexWrap:'wrap', gap:10 }}>
      <span style={{ fontSize:12, color:'#94a3b8' }}>Showing {startItem}-{endItem} of {totalItems} orders</span>
      <div style={{ display:'flex', gap:6, alignItems:'center' }}>
        <button onClick={() => onPageChange(1)}               disabled={currentPage === 1}          style={pgBtn(currentPage === 1)}>First</button>
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}          style={pgBtn(currentPage === 1)}>Prev</button>
        {getPageNumbers().map(p => <button key={p} onClick={() => onPageChange(p)} style={pgBtn(false, p === currentPage)}>{p}</button>)}
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} style={pgBtn(currentPage === totalPages)}>Next</button>
        <button onClick={() => onPageChange(totalPages)}      disabled={currentPage === totalPages} style={pgBtn(currentPage === totalPages)}>Last</button>
      </div>
    </div>
  );
};

const IconView = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const IconEdit = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const IconDelete = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
  </svg>
);

const IconDownload = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/>
  </svg>
);
const IconCalendar = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h18"/>
  </svg>
);
const IconChevronDown = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6"/>
  </svg>
);
const IconClose = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18"/><path d="M6 6l12 12"/>
  </svg>
);
const IconPackage = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73L13 2.27a2 2 0 0 0-2 0L4 6.27A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="M3.3 7L12 12l8.7-5"/><path d="M12 22V12"/>
  </svg>
);
const IconTruck = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 17h4V5H2v12h3"/><path d="M14 8h4l4 4v5h-3"/><circle cx="7.5" cy="17.5" r="2.5"/><circle cx="16.5" cy="17.5" r="2.5"/>
  </svg>
);
const IconClipboard = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="8" y="2" width="8" height="4" rx="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
  </svg>
);
const IconSave = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><path d="M17 21v-8H7v8"/><path d="M7 3v5h8"/>
  </svg>
);
const ProductThumb = ({ src, name, size }) => {
  const [err, setErr] = useState(false);
  const sz = size || 42;
  if (!src || err) {
    return (
      <div style={{ width: sz, height: sz, borderRadius: 8, flexShrink: 0, background: 'rgba(14,165,233,0.12)', border: '1px solid rgba(14,165,233,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#0e7490' }}>Box</div>
    );
  }
  return <img src={src} alt={name || 'product'} onError={() => setErr(true)} style={{ width: sz, height: sz, borderRadius: 8, objectFit: 'contain', flexShrink: 0, border: '1px solid rgba(14,165,233,0.2)', background: '#f8fafc', padding: 3, boxSizing: 'border-box' }} />;
};

const PORTAL_OVERLAY_STYLE = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(6,25,54,0.52)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 99999,
  padding: 24,
};

const PortalModal = ({ onBackdropClick, children }) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return createPortal(
    <div
      style={PORTAL_OVERLAY_STYLE}
      onMouseDown={(e) => { if (e.target === e.currentTarget && onBackdropClick) onBackdropClick(); }}
    >
      {children}
    </div>,
    document.body
  );
};


const STAT_FILTER_MAP = {
  'Total Orders':     'all',
  'Pending Orders':   'pending',
  'Completed Orders': 'delivered',
  'Cancelled Orders': 'cancelled',
};

/* Row background tokens - must be fully opaque hex/rgb */
const ROW_ODD_BG      = '#20105E';
const ROW_EVEN_BG     = '#251366';
const ROW_HOVER_BG    = '#2E1065';
const ROW_SELECTED_BG = '#3D2B7F';
const TH_BG           = '#120A3A';

const STICKY_COL_SHADOW = '-6px 0 16px -4px rgba(14,165,233,0.20)';

/* Custom Select - matches theme, portaled, natural height */
const CustomSelect = ({ value, onChange, options, disabled, triggerClassName }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = React.useRef();
  const listRef = React.useRef();

  const openList = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (ev) => {
      if (triggerRef.current && triggerRef.current.contains(ev.target)) return;
      if (listRef.current && listRef.current.contains(ev.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    const reposition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  const selected = options.find(o => o.value === value);

  return (
    <div style={{ position: 'relative', display: 'inline-block', width: triggerClassName === 'ord-cs-filter-trigger' ? 'auto' : '100%' }}>
      <button
        type="button"
        ref={triggerRef}
        className={'ord-cs-trigger ' + (triggerClassName || '')}
        disabled={disabled}
        onClick={() => { if (disabled) return; open ? setOpen(false) : openList(); }}
      >
        <span>{selected ? selected.label : 'Select'}</span>
        <span style={{ color: '#2d5f6e', marginLeft: 8, display: 'inline-flex' }}><IconChevronDown /></span>
      </button>
      {open && createPortal(
        <div
          ref={listRef}
          style={{
            position: 'fixed', top: pos.top, left: pos.left, width: pos.width,
            background: '#ffffff', border: '1px solid rgba(62,124,140,0.22)',
            borderRadius: 10, zIndex: 999999,
            boxShadow: '0 16px 40px rgba(15,23,42,0.16)',
          }}
        >
          {options.map(o => {
            const isSel = o.value === value;
            return (
              <div
                key={normalizeKeyPart(o.value) || o.label}
                onClick={() => { onChange(o.value); setOpen(false); }}
                style={{
                  padding: '9px 12px', fontSize: 13, cursor: 'pointer',
                  color: isSel ? '#ffffff' : '#102a33',
                  background: isSel ? '#3E7C8C' : '#ffffff',
                  fontFamily: "'Sora','Outfit',sans-serif",
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(ev) => { if (!isSel) ev.currentTarget.style.background = '#eef8fa'; }}
                onMouseLeave={(ev) => { if (!isSel) ev.currentTarget.style.background = '#ffffff'; }}
              >
                {o.label}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

const Orders = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [orders, setOrders]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState('');
  const [statusFilter, setStatusFilter]   = useState('all');
  const [sortBy, setSortBy]               = useState('newest');
  const [dateFrom, setDateFrom]           = useState('');
  const [dateTo, setDateTo]               = useState('');
  const [dateFilterOpen, setDateFilterOpen] = useState(false);
  const [datePreset, setDatePreset]       = useState('custom');
  const [currentPage, setCurrentPage]     = useState(1);
  const [selOrder, setSelOrder]           = useState(null);
  const [editOrder, setEditOrder]         = useState(null);
  const [queryOrderOpened, setQueryOrderOpened] = useState(false);
  const [editForm, setEditForm]           = useState({});
  const [statusMsg, setStatusMsg]         = useState('');
  const [editMsg, setEditMsg]             = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(null);
  const [saving, setSaving]               = useState(false);
  const [hoveredRow, setHoveredRow]       = useState(null);
  const [shipLoading, setShipLoading] = useState(false);
  const [shipMsg, setShipMsg]         = useState('');
  const [courierOptions, setCourierOptions] = useState(null);
  const [selectedCourierId, setSelectedCourierId] = useState(null);
  const [addressPrompt, setAddressPrompt] = useState(null);

  /* Column sort: Customer / Total Orders / Date */
  const [columnSort, setColumnSort]       = useState(null); // { key: 'customer' | 'orders' | 'date', dir: 'asc' | 'desc' }

  const toggleColumnSort = (key) => {
    setColumnSort(prev => {
      if (prev?.key === key) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      // sensible defaults: customer A-Z, orders highest first, date newest first
      const defaultDir = key === 'customer' ? 'asc' : 'desc';
      return { key, dir: defaultDir };
    });
  };

  const enrichOrdersWithCustomers = async (orderList) => {
    const missingCustomerIds = Array.from(new Set(
      orderList.map(resolveOrderCustomerId).filter(id => typeof id === 'string')
    ));
    if (!missingCustomerIds.length) return orderList;
    try {
      const res = await getAllCustomers();
      const customers = res?.data?.data || res?.data?.customers || res?.data || [];
      let customerMap = customers.reduce((acc, customer) => {
        const id = (customer && (customer._id || customer.id));
        if (id) acc[id] = customer;
        return acc;
      }, {});
      const missingIds = missingCustomerIds.filter(id => !customerMap[id]);
      if (missingIds.length) {
        const fetched = await fetchCustomersByIds(missingIds);
        customerMap = { ...customerMap, ...fetched };
      }
      return orderList.map((order) => {
        const customerId = resolveOrderCustomerId(order);
        const customer = customerId ? customerMap[customerId] : null;
        if (!customer) return order;
        const updated = { ...order, customer };
        if (typeof order.user === 'string')       updated.user = customer;
        if (typeof order.userId === 'string')     updated.userId = customer;
        if (typeof order.customer === 'string')   updated.customer = customer;
        if (typeof order.customerId === 'string') updated.customerId = customer;
        return updated;
      });
    } catch (err) {
      console.warn('Customer enrichment failed:', err);
      return orderList;
    }
  };

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await getAllOrders();
      const raw  = res?.data?.data || res?.data?.orders || res?.data || [];
      const list = Array.isArray(raw) ? raw : [];
      const activeOrders = list.filter(isOrderActive);
      const enrichedCustomers = await enrichOrdersWithCustomers(activeOrders);
      const enrichedOrders = await enrichOrdersWithProducts(enrichedCustomers);
      setOrders(enrichedOrders);
    } catch (err) {
      console.error('Orders fetch error:', err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOrders(); }, [loadOrders]);
  useEffect(() => { setCurrentPage(1); }, [search, statusFilter, sortBy, columnSort]);

  useEffect(() => {
    setQueryOrderOpened(false);
  }, [location.search]);

  useEffect(() => {
    if (queryOrderOpened || !orders.length) return;
    const orderId = new URLSearchParams(location.search).get('orderId');
    if (!orderId) return;
    const match = orders.find(o => String(o._id) === String(orderId) || String(o.id) === String(orderId));
    if (match) {
      setSelOrder(match);
      setQueryOrderOpened(true);
    }
  }, [location.search, orders, queryOrderOpened]);

  useEffect(() => {
    const handleOrderUpdated = (e) => {
      const detail = e?.detail;
      if (!detail?.id) return;
      const { id, orderStatus, status, paymentStatus, ...rest } = detail;
      setOrders(prev => prev.map(o => {
        if (o._id !== id) return o;
        return {
          ...o,
          ...(orderStatus   !== undefined ? { orderStatus, status: orderStatus } : {}),
          ...(status        !== undefined && orderStatus === undefined ? { status } : {}),
          ...(paymentStatus !== undefined ? { paymentStatus } : {}),
          ...rest,
        };
      }));
      setSelOrder(prev => {
        if (!prev || prev._id !== id) return prev;
        return {
          ...prev,
          ...(orderStatus   !== undefined ? { orderStatus, status: orderStatus } : {}),
          ...(paymentStatus !== undefined ? { paymentStatus } : {}),
          ...rest,
        };
      });
    };
    window.addEventListener('orderUpdated', handleOrderUpdated);
    return () => window.removeEventListener('orderUpdated', handleOrderUpdated);
  }, []);

  const getOrderStatus = (o) => (o.orderStatus || o.status || '').toLowerCase();
  const countStatus    = (s) => orders.filter(o => getOrderStatus(o) === s).length;
  const totalRev       = orders.filter(o => getOrderStatus(o) !== 'cancelled').reduce((s, o) => s + getOrderTotal(o), 0);

  const handleStatCardClick = (label) => {
    const filterValue = STAT_FILTER_MAP[label];
    if (filterValue === undefined) return;
    if (filterValue === 'revenue') {
      navigate('/admin/payments');
      return;
    }
    if (filterValue === 'all') {
      setStatusFilter('all');
      setSortBy('highest');
    } else {
      setStatusFilter(filterValue);
      setSortBy('newest');
    }
  };

  const formatDateLabel = (from, to) => {
    if (!from && !to) return 'Date range';
    if (from && !to) return fmtDate(from);
    if (!from && to) return fmtDate(to);
    return `${fmtDate(from)} - ${fmtDate(to)}`;
  };

  const handleDateFilterClear = () => {
    setDateFrom('');
    setDateTo('');
    setDatePreset('custom');
    setCurrentPage(1);
    setDateFilterOpen(false);
  };

  const getPresetRange = (preset) => {
    const today = new Date();
    const pad = (value) => String(value).padStart(2, '0');
    const toDateInput = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    const result = { from: '', to: toDateInput(today) };

    if (preset === 'today') {
      result.from = result.to;
    } else if (preset === 'yesterday') {
      const date = new Date(today);
      date.setDate(date.getDate() - 1);
      result.from = result.to = toDateInput(date);
    } else if (preset === '7d') {
      const date = new Date(today);
      date.setDate(date.getDate() - 6);
      result.from = toDateInput(date);
    } else if (preset === '30d') {
      const date = new Date(today);
      date.setDate(date.getDate() - 29);
      result.from = toDateInput(date);
    } else if (preset === 'this_month') {
      const date = new Date(today.getFullYear(), today.getMonth(), 1);
      result.from = toDateInput(date);
    }

    return result;
  };

  const applyDatePreset = (preset) => {
    const range = getPresetRange(preset);
    setDateFrom(range.from);
    setDateTo(range.to);
    setDatePreset(preset);
    setCurrentPage(1);
  };

  const buildExportRows = (ordersToExport) => {
    const rows = [
      ['Orders Export'],
      ['Period', dateFrom || dateTo ? formatDateLabel(dateFrom, dateTo) : 'All time'],
      ['Status', statusFilter === 'all' ? 'All status' : statusFilter],
      ['Search', search ? search : 'All'],
      ['Total orders', ordersToExport.length],
      ['Exported at', new Date().toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', hour12:true })],
      [],
      ['Order ID', 'Customer', 'Email', 'Amount', 'Order Status', 'Date'],
    ];
    ordersToExport.forEach((order) => {
      rows.push([
        order._id || '',
        getCustomerName(order),
        getCustomerEmail(order),
        getOrderTotal(order),
        getStatus(order.orderStatus || order.status, order.cancelledBy).label,
        fmtDate(order.createdAt),
      ]);
    });
    return rows;
  };

  const downloadExcel = async (rows, filename) => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Orders');

    const headerRowIndex = rows.findIndex(r => r[0] === 'Order ID');

    rows.forEach((rowData, idx) => {
      const row = sheet.addRow(rowData);

      if (idx === 0) {
        row.font = { bold: true, size: 13 };
      } else if (idx === headerRowIndex) {
        row.eachCell(cell => {
          cell.font = { bold: true };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' },
          };
        });
      } else if (idx > headerRowIndex) {
        row.eachCell(cell => {
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFD9D9D9' } },
            left: { style: 'thin', color: { argb: 'FFD9D9D9' } },
            bottom: { style: 'thin', color: { argb: 'FFD9D9D9' } },
            right: { style: 'thin', color: { argb: 'FFD9D9D9' } },
          };
        });
      } else if (idx >= 1 && idx <= 5) {
        sheet.getCell(`A${idx + 1}`).font = { bold: true };
      }
    });

    sheet.columns = [
      { width: 22 }, { width: 20 }, { width: 24 },
      { width: 12 }, { width: 14 }, { width: 14 },
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = async () => {
    if (loading) {
      alert('Data is still loading. Please wait a moment and try again.');
      return;
    }
    if (!filtered.length) {
      alert('No orders match the current filters to export.');
      return;
    }
    const rows = buildExportRows(sortedFiltered);
    await downloadExcel(rows, `orders-${dateFrom || 'all'}-${dateTo || 'all'}.xlsx`);
  };
  const statsData = [
    { lbl: 'Total Orders',     val: orders.length,            color: '#d99a2b', activeFilter: 'all',       icon: 'cart',  desc: 'All active orders' },
    { lbl: 'Pending Orders',   val: countStatus('pending'),   color: '#f59e0b', activeFilter: 'pending',   icon: 'clock', desc: 'Awaiting action' },
    { lbl: 'Completed Orders', val: countStatus('delivered'), color: '#16a34a', activeFilter: 'delivered', icon: 'check', desc: 'Delivered orders' },
    { lbl: 'Cancelled Orders', val: countStatus('cancelled'), color: '#ef4444', activeFilter: 'cancelled', icon: 'alert', desc: 'Cancelled or rejected' },
  ];

  const filtered = orders
    .filter(o => {
      const q = search.toLowerCase();
      const matchSearch = !q
        || (o._id || '').toLowerCase().includes(q)
        || getCustomerName(o).toLowerCase().includes(q)
        || getCustomerEmail(o).toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' || getOrderStatus(o) === statusFilter;
      if (!matchSearch || !matchStatus) return false;
      if (!dateFrom && !dateTo) return true;
      const created = new Date(o.createdAt || o.createdAt || 0);
      if (isNaN(created)) return false;
      if (dateFrom) {
        const start = new Date(dateFrom + 'T00:00:00');
        if (created < start) return false;
      }
      if (dateTo) {
        const end = new Date(dateTo + 'T23:59:59');
        if (created > end) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'newest')  return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      if (sortBy === 'oldest')  return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      if (sortBy === 'highest') return (b.totalAmount || b.total || 0) - (a.totalAmount || a.total || 0);
      if (sortBy === 'lowest')  return (a.totalAmount || a.total || 0) - (b.totalAmount || b.total || 0);
      return 0;
    });

  /* Apply Customer / Total Orders / Date column sort on top of the filtered list */
  const sortedFiltered = [...filtered];
  if (columnSort) {
    const dirMul = columnSort.dir === 'asc' ? 1 : -1;
    if (columnSort.key === 'customer') {
      sortedFiltered.sort((a, b) => dirMul * getCustomerName(a).localeCompare(getCustomerName(b)));
    } else if (columnSort.key === 'orders') {
      sortedFiltered.sort((a, b) => dirMul * (getOrderItemList(a).length - getOrderItemList(b).length));
    } else if (columnSort.key === 'date') {
      sortedFiltered.sort((a, b) => dirMul * (new Date(a.createdAt || 0) - new Date(b.createdAt || 0)));
    }
  }

  const totalPages = Math.ceil(sortedFiltered.length / ITEMS_PER_PAGE);
  const pageStart  = (currentPage - 1) * ITEMS_PER_PAGE;
  const pageItems  = sortedFiltered.slice(pageStart, pageStart + ITEMS_PER_PAGE);

  const getRowBg = (idx, orderId) => {
    if (selOrder && selOrder._id === orderId) return ROW_SELECTED_BG;
    if (hoveredRow === orderId)               return ROW_HOVER_BG;
    return idx % 2 === 0 ? ROW_ODD_BG : ROW_EVEN_BG;
  };

  const handleStatusChange = async (id, newStatus) => {
    const nextStatus = normalizeStatus(newStatus) || 'pending';
    const extra = nextStatus === 'cancelled' ? { cancelledBy: 'admin' } : {};
    try {
      await updateOrderStatus(id, { status: nextStatus, orderStatus: nextStatus, ...extra });
      setOrders(prev => prev.map(o => o._id === id ? { ...o, orderStatus: nextStatus, status: nextStatus, ...extra } : o));
      if (selOrder && selOrder._id === id) setSelOrder(prev => ({ ...prev, orderStatus: nextStatus, status: nextStatus, ...extra }));
      setStatusMsg('Status updated!');
      window.dispatchEvent(new CustomEvent('orderUpdated', { detail: { id, orderStatus: nextStatus, status: nextStatus, ...extra } }));
      setTimeout(() => setStatusMsg(''), 2500);
    } catch {
      setStatusMsg('Update failed');
      setTimeout(() => setStatusMsg(''), 2500);
    }
  };

  const applyShiprocketUpdate = (orderId, updated) => {
    setOrders(prev => prev.map(o => o._id === orderId ? { ...o, ...updated } : o));
    setSelOrder(prev => (prev && prev._id === orderId) ? { ...prev, ...updated } : prev);
  };

  const showIncompleteShippingAddress = (order, missingFields) => {
    const missing = missingFields && missingFields.length
      ? missingFields.map(field => {
        if (typeof field === 'string') return { key: field, label: field.replace(/_/g, ' ') };
        const key = field?.key || field?.field || field?.name || '';
        return { key, label: field?.label || key.replace(/_/g, ' ') || 'Shipping information' };
      })
      : getShippingAddressMissingFields(order);
    setAddressPrompt({ orderId: order._id, missingFields: missing });
    setShipMsg('Shipping address is incomplete. Please update the customer\'s shipping address from the appropriate customer/order management section.');
  };

  const handleCreateShipment = async (order) => {
    const missingAddressFields = getShippingAddressMissingFields(order);
    if (missingAddressFields.length) {
      showIncompleteShippingAddress(order, missingAddressFields);
      return;
    }
    if (!getOrderItemList(order).length) {
      setShipMsg('At least one product is required to create a Shiprocket shipment.');
      setTimeout(() => setShipMsg(''), 5000);
      return;
    }

    setShipLoading(true); setShipMsg(''); setAddressPrompt(null);
    try {
      const res = await createShipment(order._id);
      applyShiprocketUpdate(order._id, res.data.order || res.data.data?.order || res.data.data || {});
      setShipMsg(res?.data?.message || 'Shipment created.');
    } catch (err) {
      const data = err?.response?.data || {};
      if (data.code === 'SHIPPING_ADDRESS_INCOMPLETE' || Array.isArray(data.missingFields)) {
        showIncompleteShippingAddress(order, data.missingFields);
        return;
      }
      setShipMsg(getApiErrorMessage(err, 'Unable to create shipment.'));
    } finally {
      setShipLoading(false);
      setTimeout(() => setShipMsg(''), 5000);
    }
  };

  const handleGetCourierOptions = async (order) => {
    setShipLoading(true); setShipMsg('');
    try {
      const res = await getCourierOptions(order._id);
      const options = res?.data?.couriers || [];
      if (!options.length) {
        setShipMsg('No couriers available for this order\'s pincode/weight.');
        setCourierOptions(null);
      } else {
        setCourierOptions(options);
        setSelectedCourierId(null);
      }
    } catch (err) {
      setShipMsg('Error: ' + getApiErrorMessage(err, 'Failed to fetch courier options'));
    } finally {
      setShipLoading(false);
    }
  };

  const handleAssignAWB = async (order) => {
    if (!selectedCourierId) {
      setShipMsg('Please select a courier first.');
      return;
    }
    setShipLoading(true); setShipMsg('');
    try {
      const res = await assignAWB(order._id, selectedCourierId);
      applyShiprocketUpdate(order._id, res.data.order);
      setCourierOptions(null);
      setSelectedCourierId(null);
      setShipMsg('AWB assigned!');
    } catch (err) {
      setShipMsg('Error: ' + getApiErrorMessage(err, 'Failed to assign AWB'));
    } finally {
      setShipLoading(false);
      setTimeout(() => setShipMsg(''), 3000);
    }
  };

  const handleGetLabel = async (order) => {
    setShipLoading(true); setShipMsg('');
    try {
      const res = await getShippingLabel(order._id);
      if (res.data.labelUrl) window.open(res.data.labelUrl, '_blank');
    } catch (err) {
      setShipMsg('Error: ' + getApiErrorMessage(err, 'Failed to fetch label'));
    } finally { setShipLoading(false); }
  };

  const handleGetInvoice = async (order) => {
    setShipLoading(true); setShipMsg('');
    try {
      const res = await getShippingInvoice(order._id);
      if (res.data.invoiceUrl) window.open(res.data.invoiceUrl, '_blank');
    } catch (err) {
      setShipMsg('Error: ' + getApiErrorMessage(err, 'Failed to fetch invoice'));
    } finally { setShipLoading(false); }
  };

  const openEdit = (o) => {
    const address = getShippingAddress(o);
    setEditOrder(o);
    setEditForm({
      orderStatus:   normalizeStatus(o.orderStatus || o.status) || 'pending',
      paymentStatus: getPaymentStatus(o).toLowerCase() || 'pending',
      name:           address.name || '',
      phone:          address.phone || '',
      address:        address.address || address.rawAddress || '',
      address2:       address.address2 || '',
      city:           address.city || '',
      state:          address.state || '',
      pincode:        address.pincode || '',
    });
    setEditMsg('');
  };

  const handleEditSave = async () => {
  if (!editOrder) return;
  setSaving(true);
  try {
    const nextOrderStatus   = normalizeStatus(editForm.orderStatus) || 'pending';
    const nextPaymentStatus = (editForm.paymentStatus || 'pending').toLowerCase();
    const payload = {
      status:        nextOrderStatus,
      orderStatus:   nextOrderStatus,
      paymentStatus: nextPaymentStatus,
    };
    if (nextOrderStatus === 'cancelled') {
      payload.cancelledBy = 'admin';
    }
    const addressFields = {
      name: editForm.name.trim(),
      phone: editForm.phone.trim(),
      address: editForm.address.trim(),
      ...(editForm.address2.trim() ? { address2: editForm.address2.trim() } : {}),
      city: editForm.city.trim(),
      state: editForm.state.trim(),
      pincode: editForm.pincode.trim(),
    };
    const updateData = {
      ...payload,
      ...addressFields,
    };
    await updateOrderShippingAddress(editOrder._id, updateData);
    const updatedFields = {
      orderStatus:   nextOrderStatus,
      status:        nextOrderStatus,
      paymentStatus: nextPaymentStatus,
      ...(nextOrderStatus === 'cancelled' ? { cancelledBy: 'admin' } : {}),
      address: addressFields.address,
      shippingAddress: addressFields,
    };
    setOrders(prev => prev.map(o => o._id === editOrder._id ? { ...o, ...updatedFields } : o));
    if (selOrder && selOrder._id === editOrder._id) {
      setSelOrder(prev => prev ? { ...prev, ...updatedFields } : prev);
    }
    window.dispatchEvent(new CustomEvent('orderUpdated', { detail: { id: editOrder._id, ...updatedFields } }));
    window.dispatchEvent(new Event('adminDataUpdated'));
    await loadOrders();
    setEditMsg('Order updated successfully!');
    setTimeout(() => { setEditOrder(null); setEditMsg(''); }, 1400);
  } catch (err) {
    setEditMsg(getApiErrorMessage(err, 'Update failed. Please try again.'));
  } finally {
    setSaving(false);
  }
};

  const openDelete = (order) => {
    setDeleteConfirm({
      id: order._id,
      label: getFirstProduct(order)?.name || (order._id || '').slice(-8).toUpperCase() || 'selected order',
      order,
    });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm?.id) return;
    const targetOrder = deleteConfirm;
    try {
      await deleteOrder(targetOrder.id);
      setDeleteConfirm(null);
      setDeleteSuccess({ id: targetOrder.id, label: targetOrder.label });
    } catch {
      setDeleteConfirm(null);
      setStatusMsg('Delete failed. Please try again.');
      setTimeout(() => setStatusMsg(''), 2500);
    }
  };

  const handleDeleteSuccessClose = () => {
    const deletedId = deleteSuccess?.id;
    setDeleteSuccess(null);
    if (deletedId) {
      setOrders(prev => prev.filter(o => o._id !== deletedId));
      if (selOrder && selOrder._id === deletedId) setSelOrder(null);
      window.dispatchEvent(new Event('adminDataUpdated'));
    }
    loadOrders();
  };

  const cancelDelete = () => setDeleteConfirm(null);
  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&display=swap');

    .page-shell{position:relative;padding:6px 4px 24px;font-family:'Sora','Outfit',sans-serif;color:#111827;}
    .page-stack{position:relative;z-index:1;}
    .page-header-row{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;margin-bottom:14px;}
    .page-header-main{display:flex;flex-direction:column;gap:8px;max-width:calc(100% - 160px);}
    .page-header-title-row{display:flex;align-items:center;gap:10px;}
    .page-header-title-row h1, .orders-title{font-size:1.6rem;font-weight:800;color:var(--ink, #102a33) !important;margin:0 0 2px;letter-spacing:-0.2px;}
    .page-header-icon{display:inline-flex;align-items:center;justify-content:center;width:40px;height:40px;border-radius:14px;background:rgba(123,44,255,0.15);color:var(--secondary);box-shadow:inset 0 0 0 1px rgba(123,44,255,0.25);animation:iconFloat 5s ease-in-out infinite;}
    @keyframes iconFloat{0%,100%{transform:translateY(0);}50%{transform:translateY(-2px);}}
    .header-subtitle{font-size:13px;color:var(--text-secondary);font-weight:600;}

    .stats-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:14px;margin-bottom:24px;}
    @media(max-width:1200px){.stats-grid{grid-template-columns:repeat(3,minmax(0,1fr));}}
    @media(max-width:900px){.stats-grid{grid-template-columns:repeat(2,minmax(0,1fr));}}
    @media(max-width:650px){.stats-grid{grid-template-columns:1fr;}}
    .stat-card{border-radius:var(--radius-lg);padding:18px 18px 14px;border:1px solid var(--border-color);background:var(--card-bg);cursor:pointer;position:relative;overflow:visible;transition:all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);box-shadow:0 8px 24px rgba(123, 44, 255, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1);backdrop-filter:blur(10px);}
    .stat-card-clickable{cursor:pointer;}
    .stat-card-clickable:hover{transform:translateY(-8px) scale(1.015);box-shadow:0 16px 40px rgba(123, 44, 255, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.15);border-color:rgba(123, 44, 255, 0.3);background:var(--card-bg-hover);}
    .stat-card-active{border-width:2px !important;box-shadow:0 0 0 3px rgba(123, 44, 255, 0.1), 0 16px 40px rgba(123, 44, 255, 0.25) !important;background:var(--card-bg-hover);}
    .stat-val{font-size:1.4rem;font-weight:800;line-height:1.2;margin-bottom:5px;color:var(--text-primary);overflow-wrap:break-word;word-wrap:break-word;word-break:break-word;}
    .stat-lbl{font-size:11px;color:var(--text-secondary);font-weight:500;}
    .stat-active-dot{display:inline-block;width:6px;height:6px;border-radius:50%;margin-left:6px;vertical-align:middle;animation:pulse-dot 1.4s ease-in-out infinite;}
    @keyframes pulse-dot{0%,100%{opacity:1;transform:scale(1);}50%{opacity:.5;transform:scale(.75);}}

    .wid{background:rgba(255,255,255,0.98);border:1px solid rgba(62,124,140,0.16);border-radius:16px;padding:22px;box-shadow:0 20px 48px rgba(62,124,140,0.12);}
    .wh{display:flex;align-items:center;justify-content:space-between;margin-bottom:18px;flex-wrap:wrap;gap:12px;}
    .wt{font-size:14px;font-weight:700;color:#111827;}
    .filter-bar{position:relative;display:flex;align-items:center;gap:10px;flex-wrap:wrap;}
    .filter-input{padding:9px 14px;border-radius:10px;border:1px solid #312E81;background:#1A0B4D;color:#FFFFFF;font-family:'Sora','Outfit',sans-serif;font-size:13px;outline:none;min-width:200px;}
    .filter-input:focus{border-color:#6D28D9;box-shadow:0 0 0 3px rgba(109,40,217,0.2);}
    .filter-select{padding:9px 14px;border-radius:10px;border:1px solid #312E81;background:#1A0B4D;color:#FFFFFF;font-family:'Sora','Outfit',sans-serif;font-size:13px;cursor:pointer;outline:none;}
    .filter-select:focus{border-color:#6D28D9;box-shadow:0 0 0 3px rgba(109,40,217,0.2);}

    .tbl-scroll-wrap{overflow:auto;max-height:520px;border-radius:10px;border:1px solid rgba(62,124,140,0.16);background:#ffffff;isolation:isolate;scrollbar-width:thin;scrollbar-color:rgba(62,124,140,0.28) transparent;}
    .tbl-scroll-wrap::-webkit-scrollbar{ width:6px; height:6px; }
    .tbl-scroll-wrap::-webkit-scrollbar-track{ background:transparent; }
    .tbl-scroll-wrap::-webkit-scrollbar-thumb{ background:rgba(62,124,140,0.28); border-radius:99px; }
    .tbl-scroll-wrap::-webkit-scrollbar-corner{ background:transparent; }

    .tbl{
      width:100%;
      border-collapse:separate;
      border-spacing:0;
      table-layout:auto;
    }
    .tbl thead{
      position:sticky;
      top:0;
      z-index:10;
    }
    .tbl thead tr{ background:#f8fafc; }
    .tbl th{
      position:sticky;
      top:0;
      z-index:3;
      text-align:left;
      font-size:10px;
      font-weight:700;
      color:#2d5f6e; text-transform:uppercase; letter-spacing:.6px; padding:12px 14px; background:#f8fafc; border-bottom:1px solid rgba(62,124,140,0.16);
      white-space:nowrap;
    }
    .tbl th.sortable{cursor:pointer;user-select:none;transition:color .12s;}
    .tbl th.sortable:hover{color:#d4741a;}
    .sort-clear-icon{margin-left:6px;cursor:pointer;opacity:.65;text-transform:none;font-weight:600;}
    .sort-clear-icon:hover{opacity:1;color:#ec4899;}
    .tbl td{padding:13px 14px;font-size:13px;border-bottom:1px solid rgba(129,140,248,0.12);color:#111827;vertical-align:middle;white-space:nowrap;}
    .tbl tbody tr:last-child td{ border-bottom:none; } .tbl tbody tr:hover{background:rgba(62,124,140,0.06);}

    .th-actions{
      position:sticky !important;
      right:0 !important;
      top:0 !important;
      z-index:5 !important;
      text-align:center !important;
    }
    .td-actions{
      position:sticky !important;
      right:0 !important;
      z-index:2 !important;
      text-align:center !important;
      vertical-align:middle !important;
      padding:0 14px !important;
    }

    .ord-id{color:#2d5f6e;font-weight:700;font-size:12px;font-family:monospace;}
    .cust-name{font-weight:700;color:#111827;font-size:13px;}
    .cust-email{font-size:11px;color:#64748b;margin-top:2px;}
    .guest-tag{display:inline-block;font-size:9px;font-weight:700;padding:1px 6px;border-radius:8px;background:rgba(109,40,217,.2);color:#A855F7;border:1px solid rgba(109,40,217,.4);margin-left:5px;vertical-align:middle;}
    .bdg{display:inline-flex;align-items:center;gap:4px;font-size:11px;font-weight:700;padding:3px 10px;border-radius:20px;white-space:nowrap;}
    .bdg::before{content:'';width:5px;height:5px;border-radius:50%;background:currentColor;flex-shrink:0;}
    .bdg-g{background:rgba(52,211,153,0.08);color:#059669;border:1px solid rgba(52,211,153,0.18);}
    .bdg-b{background:rgba(14,165,233,0.08);color:#0e7490;border:1px solid rgba(14,165,233,0.18);}
    .bdg-r{background:rgba(248,113,113,0.10);color:#dc2626;border:1px solid rgba(248,113,113,0.18);}
    .bdg-y{background:rgba(245,158,11,0.10);color:#d97706;border:1px solid rgba(245,158,11,0.18);}
    .bdg-p{background:rgba(15,118,110,.1);color:#0f766e;border:1px solid rgba(15,118,110,.3);}
    .bdg-o{background:rgba(139,92,246,.15);color:#a78bfa;border:1px solid rgba(139,92,246,.3);}
    .pay-tag{font-size:11px;font-weight:700;padding:3px 9px;border-radius:6px;background:rgba(14,165,233,.12);color:#0ea5e9;border:1px solid rgba(14,165,233,.2);white-space:nowrap;}
    .empty{text-align:center;padding:40px;color:#6b7280;font-size:14px;}
    .loading{display:flex;justify-content:center;padding:40px;}
    .inf-spinner{width:26px;height:26px;border:3px solid rgba(14,165,233,.2);border-top-color:#0ea5e9;border-radius:50%;animation:spin .7s linear infinite;}
    @keyframes spin{to{transform:rotate(360deg);}}
    .row-actions{display:inline-flex;align-items:center;justify-content:center;gap:8px;}
    .row-action-btn{width:32px;height:32px;padding:0;border-radius:8px;display:inline-flex;align-items:center;justify-content:center;border:1px solid transparent;cursor:pointer;transition:background .15s ease,border-color .15s ease,transform .15s ease;}
    .row-action-btn svg{width:15px;height:15px;}
    .row-action-btn:hover{transform:translateY(-1px);}
    .row-action-view{color:#0ea5e9;background:rgba(14,165,233,.12);border-color:rgba(14,165,233,.24);}
    .row-action-view:hover{background:rgba(14,165,233,.2);border-color:rgba(14,165,233,.38);}
    .row-action-edit{color:#d99a2b;background:rgba(217,154,43,.14);border-color:rgba(217,154,43,.3);}
    .row-action-edit:hover{background:rgba(217,154,43,.22);border-color:rgba(217,154,43,.44);}
    .row-action-delete{color:#dc2626;background:rgba(220,38,38,.1);border-color:rgba(220,38,38,.22);}
    .row-action-delete:hover{background:rgba(220,38,38,.18);border-color:rgba(220,38,38,.36);}

    .act-menu{display:flex;align-items:center;justify-content:center;width:100%;height:100%;}
    .act-menu-btn{width:34px;height:34px;border-radius:10px;border:1px solid rgba(62,124,140,0.18);background:rgba(62,124,140,0.08);color:#2d5f6e;font-size:22px;line-height:1;
      cursor:pointer;transition:color .15s, background .15s, border-color .15s;
      display:flex;align-items:center;justify-content:center;
      padding:0;margin:auto;
    }
    .act-menu-btn:hover{
      color:#0ea5e9;
      background:rgba(14,165,233,0.10);
      border-color:rgba(14,165,233,0.22);
    }

    .act-menu-dd-portal .act-menu-item{
      width:100%;display:flex;align-items:center;gap:10px;
      padding:9px 10px;border:none;background:transparent;
      color:#C4B5FD;font-size:13px;font-weight:600;
      border-radius:8px;cursor:pointer;transition:background .12s;text-align:left;
      font-family:'Sora','Outfit',sans-serif;
    }
    .act-menu-dd-portal .act-menu-item:hover{ background:rgba(14,165,233,0.10); }
    .act-menu-icon{width:18px;height:18px;display:inline-flex;align-items:center;justify-content:center;color:#A855F7;}
    .act-menu-delete{color:#b91c1c !important;}
    .act-menu-delete .act-menu-icon{color:#b91c1c !important;}

    .modal-box{background:#20105E;border:1px solid #312E81;border-radius:20px;width:100%;max-width:560px;max-height:90vh;overflow-y:auto;padding:28px 28px 24px;box-shadow:0 24px 64px rgba(109,40,217,0.2);}
    .modal-hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:20px;}
    .modal-title{font-size:16px;font-weight:800;color:#FFFFFF;display:flex;align-items:center;gap:8px;}
    .modal-close{background:rgba(109,40,217,0.2);border:none;color:#C4B5FD;cursor:pointer;width:32px;height:32px;border-radius:8px;font-size:18px;display:flex;align-items:center;justify-content:center;transition:all .15s;}
    .modal-close:hover{background:rgba(109,40,217,0.3);color:#FFFFFF;}
    .order-banner{background:#20105E;border:1px solid #312E81;border-radius:14px;padding:16px 18px;margin-bottom:18px;}
    .order-banner-label{font-size:9px;font-weight:700;color:#C4B5FD;text-transform:uppercase;letter-spacing:.6px;margin-bottom:4px;}
    .order-banner-id{font-size:14px;font-weight:800;color:#FFFFFF;font-family:monospace;}
    .order-banner-sub{font-size:13px;color:#C4B5FD;margin-top:5px;font-weight:500;}
    .status-row{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:16px;}
    .status-group{border:1.5px solid rgba(109,40,217,0.4);border-radius:12px;padding:12px 14px;background:#20105E;}
    .status-group-label{font-size:9px;font-weight:700;color:#C4B5FD;text-transform:uppercase;letter-spacing:.6px;margin-bottom:8px;}
    .status-select-styled{width:100%;padding:9px 36px 9px 12px;border-radius:10px;border:1px solid rgba(62,124,140,0.28);background:#FFFFFF;color:#102a33;font-family:'Sora','Outfit',sans-serif;font-size:13px;font-weight:700;cursor:pointer;outline:none;appearance:none;-webkit-appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%232d5f6e' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 12px center;}
    .status-select-styled:focus{border-color:#3E7C8C;box-shadow:0 0 0 3px rgba(62,124,140,0.14);}
    .select-with-icon{position:relative;}
    .select-icon{position:absolute;left:10px;top:50%;transform:translateY(-50%);font-size:14px;pointer-events:none;z-index:1;}
    .select-with-icon .status-select-styled{width:100%;padding:9px 36px 9px 12px;border-radius:10px;border:1px solid rgba(62,124,140,0.28);background:#FFFFFF;color:#102a33;font-family:'Sora','Outfit',sans-serif;font-size:13px;font-weight:700;cursor:pointer;outline:none;appearance:none;-webkit-appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%232d5f6e' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 12px center;}
    .edit-field{display:flex;flex-direction:column;gap:5px;margin-bottom:14px;}
    .edit-label{font-size:9px;font-weight:700;color:#C4B5FD;text-transform:uppercase;letter-spacing:.6px;}
    .edit-input{padding:10px 13px;border-radius:9px;border:1px solid #312E81;background:#1A0B4D;color:#FFFFFF;font-family:'Sora','Outfit',sans-serif;font-size:13px;outline:none;width:100%;box-sizing:border-box;}
    .edit-input:focus{border-color:#6D28D9;box-shadow:0 0 0 3px rgba(109,40,217,0.2);}
    .char-hint{font-size:10px;color:#C4B5FD;text-align:right;margin-top:2px;}
    .msg-box{padding:10px 14px;border-radius:8px;font-size:13px;font-weight:600;margin-bottom:12px;}
    .msg-s{background:rgba(16,185,129,.15);color:#86EFAC;border:1px solid rgba(16,185,129,.3);}
    .msg-e{background:rgba(239,68,68,.15);color:#FCA5A5;border:1px solid rgba(239,68,68,.3);}
    .filter-date-btn{display:inline-flex;align-items:center;gap:10px;padding:10px 14px;border-radius:12px;border:1px solid #312E81;background:rgba(255,255,255,0.04);color:#C4B5FD;font-size:13px;font-weight:700;cursor:pointer;transition:all .15s;white-space:nowrap;}
    .filter-date-btn:hover{background:rgba(255,255,255,0.08);}
    .filter-date-btn span { display:inline-flex; align-items:center; }
    .date-filter-overlay{position:absolute;top:100%;right:0;width:min(420px,100%);margin-top:12px;z-index:999;}
    .date-filter-popup{width:100%;box-sizing:border-box;border-radius:20px;background:#20105E;border:1px solid #312E81;padding:22px;box-shadow:0 24px 60px rgba(0,0,0,0.28);overflow:auto;}
    .date-filter-popup-header{font-size:14px;font-weight:700;color:#ffffff;margin-bottom:14px;}
    .date-filter-summary{font-size:12px;color:#c4b5fd;margin-bottom:16px;}
    .date-filter-fields{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px;}
    .date-filter-field{display:flex;flex-direction:column;gap:6px;}
    .date-filter-label{font-size:11px;font-weight:700;color:#c4b5fd;text-transform:uppercase;letter-spacing:.4px;}
    .date-filter-input{padding:12px 14px;border-radius:12px;border:1px solid #312E81;background:#15123f;color:#ffffff;font-family:'Sora','Outfit',sans-serif;font-size:13px;outline:none;}
    .date-filter-input::-webkit-calendar-picker-indicator{filter: invert(1) brightness(2) contrast(0.8);}
    .date-filter-presets{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:18px;}
    .date-filter-preset-btn{padding:10px 14px;border-radius:999px;border:1px solid #312E81;background:rgba(255,255,255,0.03);color:#c4b5fd;font-size:12px;font-weight:700;cursor:pointer;transition:all .15s;}
    .date-filter-preset-btn:hover{background:rgba(109,40,217,0.18);}
    .date-filter-preset-btn.is-active{background:rgba(109,40,217,0.25);border-color:#7C3AED;color:#FFFFFF;}
    .date-filter-actions{display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;}
    .date-filter-clear{padding:10px 14px;border-radius:12px;border:1px solid #312E81;background:transparent;color:#c4b5fd;cursor:pointer;}
    .date-filter-export{padding:10px 14px;border-radius:12px;border:1px solid #312E81;background:rgba(109,40,217,0.18);color:#c4b5fd;cursor:pointer;}
    .date-filter-export:hover{background:rgba(109,40,217,0.28);}
    .date-filter-apply{padding:10px 14px;border-radius:12px;border:none;background:linear-gradient(135deg,#6D28D9,#A855F7);color:#fff;cursor:pointer;}
    .filter-export-btn{display:inline-flex;align-items:center;gap:8px;padding:9px 14px;border-radius:10px;border:1px solid #312E81;background:rgba(109,40,217,0.18);color:#ffffff;font-size:13px;font-weight:700;cursor:pointer;transition:all .15s;white-space:nowrap;}
    .filter-export-btn:hover{background:rgba(109,40,217,0.28);}
    .filter-export-btn:disabled{opacity:.5;cursor:not-allowed;}
    .modal-footer{display:flex;gap:10px;justify-content:flex-end;margin-top:16px;}
    .btn{padding:10px 22px;border-radius:10px;font-family:'Sora','Outfit',sans-serif;font-size:13px;font-weight:700;cursor:pointer;border:none;transition:all .15s;}
    .btn-primary{background:linear-gradient(135deg,#6D28D9,#A855F7);color:#fff;box-shadow:0 8px 18px rgba(109,40,217,.3);}
    .btn-primary:disabled{opacity:.5;cursor:not-allowed;}
    .btn-ghost{background:transparent;border:1px solid #312E81;color:#C4B5FD;}
    .btn-ghost:hover{background:rgba(109,40,217,0.15);}
    .btn-ghost:disabled{opacity:.5;cursor:not-allowed;}
    .btn-danger{background:rgba(239,68,68,.15);color:#FCA5A5;border:1px solid rgba(239,68,68,.3);}
    .btn-danger:hover{background:rgba(239,68,68,.25);}
    .detail-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px;}
    .detail-full{grid-column:1/-1;}
    .detail-item{background:#20105E;border-radius:10px;padding:13px 16px;border:1px solid #312E81;}
    .detail-label{font-size:10px;font-weight:700;color:#C4B5FD;text-transform:uppercase;letter-spacing:.5px;margin-bottom:5px;}
    .detail-val{font-size:14px;font-weight:600;color:#FFFFFF;}
    .items-box{background:#20105E;border-radius:10px;padding:16px;margin-bottom:16px;border:1px solid #312E81;}
    .items-title{font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:.5px;margin-bottom:10px;}
    .item-row{display:flex;align-items:center;gap:12px;padding:11px 0;border-bottom:1px solid rgba(14,165,233,0.08);}
    .item-row:last-child{border-bottom:none;}
    .item-info{flex:1;min-width:0;}
    .item-name{font-size:13px;font-weight:700;color:var(--text-primary);margin-bottom:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
    .item-desc{font-size:11px;color:#555;line-height:1.4;margin-bottom:5px;overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;font-weight:500;}
    .item-meta{display:flex;align-items:center;gap:8px;}
    .item-qty{font-size:11px;font-weight:700;padding:2px 8px;border-radius:5px;background:rgba(14,165,233,0.16);color:#0ea5e9;}
    .item-price{font-size:13px;font-weight:800;color:#0ea5e9;}
    .item-unit{font-size:11px;color:#6b7280;}
    .status-select{width:100%;padding:9px 12px;border-radius:8px;border:1px solid #312E81;background:rgba(26,16,69,0.8);color:var(--text-primary);font-family:'Sora','Outfit',sans-serif;font-size:13px;cursor:pointer;outline:none;}
    .status-select:focus{border-color:#0ea5e9;box-shadow:0 0 0 3px rgba(14,165,233,0.18);}
    .status-select:disabled{opacity:.5;cursor:not-allowed;}
    .ord-cs-trigger{width:100%;text-align:left;cursor:pointer;padding:9px 12px;border-radius:10px;border:1px solid rgba(62,124,140,0.28);background:#FFFFFF;color:#102a33;font-family:'Sora','Outfit',sans-serif;font-size:13px;font-weight:700;display:flex;justify-content:space-between;align-items:center;box-sizing:border-box;box-shadow:0 1px 2px rgba(15,23,42,0.04);}
    .ord-cs-trigger:hover{border-color:#3E7C8C;box-shadow:0 0 0 3px rgba(62,124,140,0.14);}
    .ord-cs-trigger:disabled{opacity:.62;cursor:not-allowed;background:#f1f5f9;color:#94a3b8;}
    .ord-cs-filter-trigger{width:auto;min-width:170px;padding:9px 14px;border-radius:10px;background:#FFFFFF;color:#102a33;}
    .ord-cs-styled-trigger{font-weight:600;background:#1A0B4D;}
    .ord-cs-styled-trigger.with-icon{padding-left:30px;}
    /* ISHMAI Orders modal theme */
    .modal-box{background:#ffffff;border:1px solid #d8dee9;border-radius:14px;width:100%;max-width:760px;max-height:90vh;overflow-y:auto;padding:26px 28px 22px;box-shadow:0 30px 90px rgba(6,25,54,.24);color:#0f172a;}
    .modal-hdr{display:flex;align-items:center;justify-content:space-between;margin:-26px -28px 20px;padding:18px 22px;border-bottom:1px solid #e5e7eb;background:#ffffff;border-radius:14px 14px 0 0;}
    .modal-title{font-size:16px;font-weight:800;color:#0b1f3a;display:flex;align-items:center;gap:8px;}
    .modal-title svg{color:#d99a2b;}
    .modal-close{background:#f5f7fb;border:1px solid #d8dee9;color:#0b1f3a;cursor:pointer;width:34px;height:34px;border-radius:10px;font-size:18px;display:flex;align-items:center;justify-content:center;transition:all .15s;}
    .modal-close:hover{background:#fff7e6;border-color:rgba(217,154,43,.45);color:#b7791f;}
    .order-banner{background:#f8fafc;border:1px solid #e5e7eb;border-left:4px solid #d99a2b;border-radius:12px;padding:14px 16px;margin-bottom:16px;}
    .order-banner-label{font-size:10px;font-weight:800;color:#64748b;text-transform:uppercase;letter-spacing:.6px;margin-bottom:4px;}
    .order-banner-id{font-size:14px;font-weight:800;color:#0b1f3a;font-family:monospace;}
    .order-banner-sub{font-size:13px;color:#64748b;margin-top:5px;font-weight:600;}
    .status-row{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:16px;}
    .status-group{border:1px solid #e5e7eb;border-radius:12px;padding:13px 14px;background:#f8fafc;}
    .status-group-label,.edit-label{font-size:10px;font-weight:800;color:#64748b;text-transform:uppercase;letter-spacing:.6px;margin-bottom:8px;}
    .select-with-icon{position:relative;}
    .select-icon{position:absolute;left:10px;top:50%;transform:translateY(-50%);font-size:14px;pointer-events:none;z-index:1;color:#d99a2b;display:inline-flex;}
    .edit-field{display:flex;flex-direction:column;gap:6px;margin-bottom:14px;}
    .edit-address-section{border-top:1px solid #e5e7eb;padding-top:16px;margin-top:4px;}
    .edit-section-title{font-size:10px;font-weight:800;color:#64748b;text-transform:uppercase;letter-spacing:.6px;margin-bottom:12px;}
    .edit-address-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));column-gap:14px;}
    .edit-field-wide{grid-column:1/-1;}
    .edit-textarea{min-height:74px;resize:vertical;line-height:1.45;}
    .edit-optional{font-size:10px;font-weight:600;text-transform:none;letter-spacing:0;color:#94a3b8;}
    .edit-input{padding:11px 13px;border-radius:10px;border:1px solid #d8dee9;background:#ffffff;color:#0f172a;font-family:'Sora','Outfit',sans-serif;font-size:13px;outline:none;width:100%;box-sizing:border-box;}
    .edit-input:focus,.ord-cs-trigger:focus{border-color:#d99a2b;box-shadow:0 0 0 4px rgba(217,154,43,.14);}
    .char-hint{font-size:10px;color:#64748b;text-align:right;margin-top:2px;}
    .detail-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px;}
    .detail-full{grid-column:1/-1;}
    .detail-item{background:#f8fafc;border-radius:12px;padding:13px 16px;border:1px solid #e5e7eb;}
    .detail-label{font-size:10px;font-weight:800;color:#64748b;text-transform:uppercase;letter-spacing:.5px;margin-bottom:5px;}
    .detail-val{font-size:14px;font-weight:700;color:#0f172a;}
    .items-box{background:#ffffff;border-radius:12px;padding:16px;margin-bottom:16px;border:1px solid #e5e7eb;box-shadow:0 8px 24px rgba(15,23,42,.04);}
    .items-title{font-size:10px;font-weight:800;color:#64748b;text-transform:uppercase;letter-spacing:.6px;margin-bottom:12px;}
    .item-row{display:flex;align-items:center;gap:12px;padding:11px 0;border-bottom:1px solid #e5e7eb;}
    .item-row:last-child{border-bottom:none;}
    .item-name{font-size:13px;font-weight:800;color:#0f172a;margin-bottom:3px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
    .item-meta{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
    .item-qty{font-size:11px;font-weight:800;padding:3px 8px;border-radius:6px;background:#eef6ff;color:#0b4b77;border:1px solid #cfe7ff;}
    .item-price{font-size:13px;font-weight:800;color:#d99a2b;}
    .item-unit{font-size:11px;color:#64748b;}
    .ord-cs-trigger{width:100%;text-align:left;cursor:pointer;padding:10px 12px;border-radius:10px;border:1px solid #d8dee9;background:#ffffff;color:#0f172a;font-family:'Sora','Outfit',sans-serif;font-size:13px;font-weight:700;display:flex;justify-content:space-between;align-items:center;box-sizing:border-box;box-shadow:none;}
    .ord-cs-trigger:hover{border-color:#d99a2b;box-shadow:0 0 0 4px rgba(217,154,43,.12);}
    .ord-cs-trigger:disabled{opacity:.62;cursor:not-allowed;background:#f1f5f9;color:#94a3b8;}
    .ord-cs-styled-trigger{font-weight:700;background:#ffffff;color:#0f172a;}
    .ord-cs-styled-trigger.with-icon{padding-left:34px;}
    .modal-footer{display:flex;gap:10px;justify-content:flex-end;margin-top:18px;padding-top:16px;border-top:1px solid #e5e7eb;}
    .btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;padding:10px 20px;border-radius:10px;font-family:'Sora','Outfit',sans-serif;font-size:13px;font-weight:800;cursor:pointer;border:1px solid transparent;transition:all .15s;}
    .btn-primary{background:#d99a2b;color:#ffffff;border-color:#d99a2b;box-shadow:0 10px 24px rgba(217,154,43,.2);}
    .btn-primary:hover{background:#b7791f;border-color:#b7791f;transform:translateY(-1px);}
    .btn-primary:disabled{opacity:.55;cursor:not-allowed;transform:none;}
    .btn-ghost{background:#ffffff;border:1px solid #d8dee9;color:#0b1f3a;}
    .btn-ghost:hover{background:#f8fafc;border-color:#d99a2b;color:#b7791f;}
    .btn-ghost:disabled{opacity:.55;cursor:not-allowed;}
    .btn-danger{background:#fff5f5;color:#dc2626;border:1px solid #fecaca;}
    .btn-danger:hover{background:#fee2e2;border-color:#fca5a5;color:#b91c1c;transform:translateY(-1px);}
    .msg-s{background:#ecfdf5;color:#047857;border:1px solid #a7f3d0;}
    .msg-e{background:#fef2f2;color:#b91c1c;border:1px solid #fecaca;}
    .order-detail-sections{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;margin-bottom:16px;}
    .order-detail-card{min-width:0;background:#f8fafc;border:1px solid #e5e7eb;border-top:3px solid #d99a2b;border-radius:12px;padding:15px 16px;}
    .order-detail-card-wide{grid-column:1/-1;}
    .order-detail-card h3{margin:0 0 13px;color:#0b1f3a;font-size:12px;font-weight:800;letter-spacing:.5px;text-transform:uppercase;}
    .order-detail-fields{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:11px 20px;}
    .order-detail-fields-single{grid-template-columns:1fr;gap:10px;}
    .order-detail-field{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;min-width:0;font-size:12px;color:#64748b;line-height:1.45;}
    .order-detail-field span{flex:0 0 auto;}
    .order-detail-field strong{min-width:0;color:#0f172a;font-size:13px;text-align:right;overflow-wrap:anywhere;}
    .order-detail-field-wide{grid-column:1/-1;display:block;}
    .order-detail-field-wide strong{display:block;margin-top:4px;text-align:left;}
    .order-detail-saving strong{color:#059669;}
    .order-detail-amount strong,.order-detail-total strong{color:#b7791f;font-size:15px;}
    .order-detail-total{grid-column:1/-1;border-top:1px solid #e5e7eb;padding-top:11px;margin-top:2px;}
    .order-detail-note{grid-column:1/-1;color:#047857;font-size:11px;font-weight:700;}
    .order-detail-warning{color:#b45309;margin-top:8px;}
    .legacy-order-summary{display:none;}
    @media(max-width:640px){.modal-box{padding:22px 18px;}.modal-hdr{margin:-22px -18px 18px;padding:16px 18px;}.detail-grid,.status-row,.order-detail-sections,.edit-address-grid{grid-template-columns:1fr;}.order-detail-card-wide,.edit-field-wide{grid-column:auto;}.order-detail-fields{grid-template-columns:1fr;}.order-detail-field-wide,.order-detail-total{grid-column:auto;}.modal-footer{justify-content:stretch;}.modal-footer .btn{flex:1;}}
  `;

  return (
    <AdminLayout>
      <style>{css}</style>
      <div className="page-shell">
        <div className="page-stack">

          {/* Page Header */}
          <div className="page-header-row">
            <div className="page-header-main">
              <div className="page-header-title-row">
                <div className="page-header-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6H4l-2 9h20l-2-9z" /><path d="M7 6V4a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" />
                  </svg>
                </div>
                <div>
                  <h1 className="orders-title ptitle" style={{ fontSize:'1.6rem', fontWeight:800, color:'#102a33', margin:'0 0 2px', letterSpacing:'-0.2px' }}>Orders</h1>
                  <div className="header-subtitle">Track &amp; manage all your customer orders in one place</div>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="filter-export-btn"
              disabled={loading}
              onClick={handleExportCSV}
              style={{ alignSelf: 'flex-start', marginTop: 4 }}
            >
              {loading ? 'Loading...' : <><IconDownload /> Export Excel</>}
            </button>
          </div>

          {statusMsg && (
            <div className={'msg-box ' + (!statusMsg.toLowerCase().includes('failed') ? 'msg-s' : 'msg-e')} style={{ marginBottom: 16 }}>
              {statusMsg}
            </div>
          )}

          {/* Stats */}
          <StatCards
            cards={statsData.map(s => ({
              title: s.lbl,
              value: s.val,
              description: s.desc,
              icon: s.icon,
              accent: s.color,
              active: statusFilter === s.activeFilter,
              onClick: () => handleStatCardClick(s.lbl),
            }))}
          />

          {/* Table Widget */}
          <div className="wid">
            <div className="wh">
              <span className="wt">All Orders ({sortedFiltered.length})</span>
              <div className="filter-bar">
                <input
                  className="filter-input"
                  placeholder="Search by ID or customer..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                <button
                  type="button"
                  className="filter-date-btn"
                  onClick={() => setDateFilterOpen(prev => !prev)}
                >
                  <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                    <IconCalendar />
                    {formatDateLabel(dateFrom, dateTo)}
                  </span>
                  {(dateFrom || dateTo) && (
                    <span
                      style={{ marginLeft:10, fontSize:14, cursor:'pointer' }}
                      onClick={(e) => { e.stopPropagation(); handleDateFilterClear(); }}
                      title="Clear date filter"
                    ><IconClose /></span>
                  )}
                </button>
                <CustomSelect
                  triggerClassName="ord-cs-filter-trigger"
                  value={statusFilter}
                  onChange={val => { setStatusFilter(val); setSortBy('newest'); }}
                  options={[
                    { value: 'all', label: 'All Status' },
                    { value: 'pending', label: 'Pending' },
                    { value: 'confirmed', label: 'Confirmed' },
                    { value: 'shipped', label: 'Shipped' },
                    { value: 'delivered', label: 'Delivered' },
                    { value: 'cancelled', label: 'Rejected' },
                  ]}
                />
                <CustomSelect
                  triggerClassName="ord-cs-filter-trigger"
                  value={sortBy}
                  onChange={setSortBy}
                  options={[
                    { value: 'newest', label: 'Newest First' },
                    { value: 'oldest', label: 'Oldest First' },
                    { value: 'highest', label: 'Highest Amount' },
                    { value: 'lowest', label: 'Lowest Amount' },
                  ]}
                />
                {dateFilterOpen && (
                  <div className="date-filter-overlay" onClick={() => setDateFilterOpen(false)}>
                    <div className="date-filter-popup" onClick={e => e.stopPropagation()}>
                      <div className="date-filter-popup-header">Filter by Date</div>
                      <div className="date-filter-summary">Use quick selects or choose a custom date range.</div>
                      <div className="date-filter-presets">
                        {[
                          { key: 'today', label: 'Today' },
                          { key: 'yesterday', label: 'Yesterday' },
                          { key: '7d', label: 'Last 7 Days' },
                          { key: '30d', label: 'Last 30 Days' },
                          { key: 'this_month', label: 'This Month' },
                          { key: 'custom', label: 'Custom' },
                        ].map(preset => (
                          <button
                            key={preset.key}
                            type="button"
                            className={['date-filter-preset-btn', datePreset === preset.key ? 'is-active' : ''].join(' ')}
                            onClick={() => applyDatePreset(preset.key)}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                      <div className="date-filter-fields">
                        <div className="date-filter-field">
                          <div className="date-filter-label">Start date</div>
                          <input
                            type="date"
                            className="date-filter-input"
                            value={dateFrom}
                            onChange={e => { setDateFrom(e.target.value); setDatePreset('custom'); }}
                          />
                        </div>
                        <div className="date-filter-field">
                          <div className="date-filter-label">End date</div>
                          <input
                            type="date"
                            className="date-filter-input"
                            value={dateTo}
                            onChange={e => { setDateTo(e.target.value); setDatePreset('custom'); }}
                          />
                        </div>
                      </div>
                      <div className="date-filter-actions">
                        <button type="button" className="date-filter-clear" onClick={handleDateFilterClear}>Clear</button>
                        <button type="button" className="date-filter-apply" onClick={() => { setDateFilterOpen(false); setCurrentPage(1); }}>Apply</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {loading ? (
              <div className="loading"><div className="inf-spinner" /></div>
            ) : (
              <>
                <div className="tbl-scroll-wrap">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th style={{ minWidth:36,  background: TH_BG }}>#</th>
                        <th style={{ minWidth:110, background: TH_BG }}>Order ID</th>
                        <th
                          className="sortable"
                          style={{ minWidth:180, background: TH_BG }}
                          onClick={() => toggleColumnSort('customer')}
                        >
                          Customer{columnSort?.key === 'customer' ? (columnSort.dir === 'asc' ? ' Asc' : ' Desc') : ''}
                          {columnSort?.key === 'customer' && (
                            <span
                              className="sort-clear-icon"
                              onClick={(e) => { e.stopPropagation(); setColumnSort(null); }}
                              title="Clear sort"
                            ><IconClose /></span>
                          )}
                        </th>
                        <th
                          className="sortable"
                          style={{ minWidth:130, background: TH_BG }}
                          onClick={() => toggleColumnSort('orders')}
                        >
                          Total Orders{columnSort?.key === 'orders' ? (columnSort.dir === 'asc' ? ' Asc' : ' Desc') : ''}
                          {columnSort?.key === 'orders' && (
                            <span
                              className="sort-clear-icon"
                              onClick={(e) => { e.stopPropagation(); setColumnSort(null); }}
                              title="Clear sort"
                            ><IconClose /></span>
                          )}
                        </th>
                        <th style={{ minWidth:110, background: TH_BG }}>Pay Method</th>
                        <th style={{ minWidth:110, background: TH_BG }}>Pay Status</th>
                        <th style={{ minWidth:130, background: TH_BG }}>Order Status</th>
                        <th
                          className="sortable"
                          style={{ minWidth:100, background: TH_BG }}
                          onClick={() => toggleColumnSort('date')}
                        >
                          Date{columnSort?.key === 'date' ? (columnSort.dir === 'asc' ? ' Asc' : ' Desc') : ''}
                          {columnSort?.key === 'date' && (
                            <span
                              className="sort-clear-icon"
                              onClick={(e) => { e.stopPropagation(); setColumnSort(null); }}
                              title="Clear sort"
                            ><IconClose /></span>
                          )}
                        </th>
                        <th
                          className="th-actions"
                          style={{
                            minWidth:   132,
                            background: TH_BG,
                            boxShadow:  STICKY_COL_SHADOW,
                            borderLeft: '1px solid rgba(14,165,233,0.18)',
                          }}
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageItems.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="empty" style={{ background: ROW_ODD_BG }}>
                            No orders found.
                          </td>
                        </tr>
                      ) : (
                        pageItems.map((o, idx) => {
                          const sm           = getStatus(o.orderStatus || o.status, o.cancelledBy);
                          const payStatus    = getPaymentStatus(o).toLowerCase();
                          const payStatusCls = getPaymentStatusClass(payStatus);
                          const custName     = getCustomerName(o);
                          const custEmail    = getCustomerEmail(o);
                          const isGuest      = custName === 'Guest';
                          const rowBg        = getRowBg(idx, o._id);

                          return (
                            <tr
                              key={makeSafeKey('order', o, idx)}
                              onClick={() => setSelOrder(o)}
                              onMouseEnter={() => setHoveredRow(o._id)}
                              onMouseLeave={() => setHoveredRow(null)}
                              style={{ cursor:'pointer' }}
                            >
                              <td style={{ color:'#ffffff', fontSize:12, background: rowBg }}>{pageStart + idx + 1}</td>
                              <td style={{ background: rowBg }}>
                                <span className="ord-id">#{(o._id || '').slice(-8).toUpperCase()}</span>
                              </td>
                              <td style={{ background: rowBg }}>
                                <div className="cust-name">
                                  {custName}
                                  {isGuest && <span className="guest-tag">GUEST</span>}
                                </div>
                                {custEmail && <div className="cust-email">{custEmail}</div>}
                              </td>
                              <td style={{ color:'#FFFFFF', fontSize:13, fontWeight:600, background: rowBg }}>
                                {getOrderItemList(o).length}
                              </td>
                              <td style={{ background: rowBg }}>
                                <span className="pay-tag">{(o.paymentMethod || 'COD').toUpperCase()}</span>
                              </td>
                              <td style={{ background: rowBg }}>
                                <span className={'bdg ' + payStatusCls}>{payStatus ? payStatus.toUpperCase() : '-'}</span>
                              </td>
                              <td style={{ background: rowBg }}>
                                <span className={'bdg ' + sm.cls}>{sm.label}</span>
                              </td>
                              <td style={{ color:'#94a3b8', fontSize:12, background: rowBg }}>
                                {fmtDate(o.createdAt)}
                              </td>
                              <td
                                className="td-actions"
                                style={{
                                  background: rowBg,
                                  boxShadow:  STICKY_COL_SHADOW,
                                  borderLeft: '1px solid rgba(14,165,233,0.10)',
                                }}
                                onClick={e => e.stopPropagation()}
                              >
                                <div className="row-actions"><button type="button" className="row-action-btn row-action-view" title="View" aria-label="View" onClick={() => setSelOrder(o)}><IconView /></button><button type="button" className="row-action-btn row-action-edit" title="Edit" aria-label="Edit" onClick={() => openEdit(o)}><IconEdit /></button><button type="button" className="row-action-btn row-action-delete" title="Delete" aria-label="Delete" onClick={() => openDelete(o)}><IconDelete /></button></div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  totalItems={sortedFiltered.length}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* View Modal */}
      {selOrder && (
        <PortalModal onBackdropClick={() => setSelOrder(null)}>
          <div className="modal-box">
            <div className="modal-hdr">
              <span className="modal-title"><IconPackage /> Order Details</span>
              <button className="modal-close" onClick={() => setSelOrder(null)}><IconClose /></button>
            </div>
            {(() => {
              const address = getShippingAddress(selOrder);
              const items = getOrderItemList(selOrder);
              const superCoins = getSuperCoinDetails(selOrder);
              const giftPacking = getGiftPackingDetails(selOrder);
              const paymentId = getPaymentId(selOrder);
              const paymentStatus = getPaymentStatus(selOrder);
              const refundDetails = getRefundDetails(selOrder);
              const subtotal = getOrderField(selOrder, ['subtotal', 'subTotal', 'subtotalAmount']) ?? (items.length ? getOrderSubtotal(selOrder) : undefined);
              const discount = getOrderField(selOrder, ['discountAmount', 'couponDiscount', 'couponDiscountAmount']);
              const hasDiscount = discount !== undefined || getOrderCouponCode(selOrder) || selOrder.coupon || selOrder.appliedCoupon || selOrder.couponDetails;
              const finalTotal = getOrderTotal(selOrder);
              const field = (label, value, className = '') => value !== undefined && value !== null && String(value).trim() !== '' && (
                <div className={'order-detail-field ' + className}>
                  <span>{label}</span>
                  <strong>{value}</strong>
                </div>
              );
              return (
                <div className="order-detail-sections">
                  <section className="order-detail-card order-detail-card-wide">
                    <h3>Customer Details</h3>
                    <div className="order-detail-fields">
                      {field('Full Name', getCustomerName(selOrder) !== 'Guest' ? getCustomerName(selOrder) : undefined)}
                      {field('Mobile Number', getCustomerPhone(selOrder))}
                      {field('Email', getCustomerEmail(selOrder))}
                      {field('City', address.city)}
                      {field('State', address.state)}
                      {field('Pincode', address.pincode)}
                      {(address.address || address.address2 || address.rawAddress) && (
                        <div className="order-detail-field order-detail-field-wide"><span>Complete Shipping Address</span><strong>{[address.address || address.rawAddress, address.address2].filter(Boolean).join(', ')}</strong></div>
                      )}
                    </div>
                  </section>
                  <section className="order-detail-card">
                    <h3>Order Information</h3>
                    <div className="order-detail-fields order-detail-fields-single">
                      {field('Order ID', selOrder._id ? '#' + selOrder._id.slice(-8).toUpperCase() : undefined)}
                      {field('Order Date', selOrder.createdAt ? fmtDate(selOrder.createdAt) : undefined)}
                      {field('Order Status', getOrderStatus(selOrder) ? getStatus(getOrderStatus(selOrder), selOrder.cancelledBy).label : undefined)}
                      {field('Payment Method', selOrder.paymentMethod ? String(selOrder.paymentMethod).toUpperCase() : undefined)}
                      {field('Payment Status', paymentStatus ? paymentStatus.toUpperCase() : undefined)}
                    </div>
                  </section>
                  <section className="order-detail-card">
                    <h3>Payment Details</h3>
                    <div className="order-detail-fields order-detail-fields-single">
                      {field('Payment Method', selOrder.paymentMethod ? String(selOrder.paymentMethod).toUpperCase() : undefined)}
                      {field('Payment Status', paymentStatus ? paymentStatus.toUpperCase() : undefined)}
                      {field('Refund Status', refundDetails.status ? String(refundDetails.status).toUpperCase() : undefined)}
                      {field('Refund ID', refundDetails.id)}
                      {field('Refund Amount', refundDetails.amount !== undefined ? fmtINR(refundDetails.amount) : undefined)}
                      {field('Refund Date', refundDetails.date ? fmtDate(refundDetails.date) : undefined)}
                      {field('Transaction / Payment ID', paymentId)}
                      {field('Order Total', fmtINR(finalTotal), 'order-detail-amount')}
                    </div>
                  </section>
                  <section className="order-detail-card order-detail-card-wide">
                    <h3>Discount &amp; ISHMAI Coins</h3>
                    <div className="order-detail-fields">
                      {field('Original / Subtotal Amount', subtotal !== undefined ? fmtINR(subtotal) : undefined)}
                      {hasDiscount && field('Discount Amount', fmtINR(Number(discount) || getOrderCouponDiscount(selOrder)), 'order-detail-saving')}
                      {superCoins.hasValue && field('ISHMAI Coins Used', superCoins.used)}
                      {superCoins.hasValue && field('ISHMAI Coins Discount / Amount', superCoins.amount !== undefined ? '-' + fmtINR(superCoins.amount) : undefined, 'order-detail-saving')}
                      {superCoins.amount !== undefined && <div className="order-detail-note">ISHMAI Coins amount deducted from the order total.</div>}
                      {field('Remaining / Final Payable Amount', fmtINR(finalTotal), 'order-detail-total')}
                    </div>
                  </section>
                  {giftPacking.hasValue && (
                    <section className="order-detail-card">
                      <h3>Gift Packing</h3>
                      <div className="order-detail-fields order-detail-fields-single">
                        {field('Gift Packing', giftPacking.enabled ? 'Yes' : 'No')}
                        {field('Gift Packing Amount', giftPacking.amount !== undefined ? fmtINR(giftPacking.amount) : undefined)}
                      </div>
                    </section>
                  )}
                  <section className="order-detail-card order-detail-card-wide">
                    <h3>Update Order Status</h3>
                    {(() => {
                      const currentStatus = normalizeStatus(selOrder.orderStatus || selOrder.status) || 'pending';
                      const options = getOrderStatusOptions(currentStatus);
                      const locked = isOrderStatusLocked(currentStatus);
                      return <><CustomSelect value={currentStatus} disabled={locked} onChange={val => handleStatusChange(selOrder._id, val)} options={options.map(opt => ({ value: opt, label: ORDER_STATUS_LABELS[opt] }))} />{locked && <div className="order-detail-note order-detail-warning">This order is in a final state - status can't be changed.</div>}</>;
                    })()}
                  </section>
                </div>
              );
            })()}
            <div className="detail-grid legacy-order-summary">
              <div className="detail-item">
                <div className="detail-label">Order ID</div>
                <div className="detail-val" style={{ color:'#d99a2b', fontFamily:'monospace', fontSize:13 }}>#{(selOrder._id || '').slice(-8).toUpperCase()}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Status</div>
                <div style={{ marginTop:4 }}><span className={'bdg ' + getStatus(selOrder.orderStatus || selOrder.status, selOrder.cancelledBy).cls}>{getStatus(selOrder.orderStatus || selOrder.status, selOrder.cancelledBy).label}</span></div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Customer</div>
                <div className="detail-val">{getCustomerName(selOrder)}</div>
                {getCustomerEmail(selOrder) && <div style={{ fontSize:11, color:'#94a3b8', marginTop:3 }}>{getCustomerEmail(selOrder)}</div>}
                {getCustomerPhone(selOrder) && <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>{getCustomerPhone(selOrder)}</div>}
              </div>
              <div className="detail-item">
                <div className="detail-label">Total Amount</div>
                <div className="detail-val" style={{ color:'#d99a2b' }}>{fmtINR(selOrder.totalAmount || selOrder.total)}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Payment</div>
                <div className="detail-val">
                  {(selOrder.paymentMethod || 'COD').toUpperCase()}
                  {getPaymentStatus(selOrder) && <span style={{ marginLeft:8, fontSize:11, color: getPaymentStatusClass(getPaymentStatus(selOrder)) === 'bdg-g' ? '#34d399' : '#fbbf24' }}>- {getPaymentStatus(selOrder).toUpperCase()}</span>}
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Order Date</div>
                <div className="detail-val" style={{ fontSize:13 }}>{fmtDate(selOrder.createdAt)}</div>
              </div>
              {(() => {
                const address = getShippingAddress(selOrder);
                const missing = getShippingAddressMissingFields(selOrder);
                const lineStyle = { fontSize:12, color:'#334155', lineHeight:1.55 };
                return (
                  <div className="detail-item detail-full">
                    <div className="detail-label">Shipping Address</div>
                    {missing.length > 0 && (
                      <div style={{ margin:'4px 0 8px', fontSize:12, color:'#b45309', fontWeight:800 }}>
                        Incomplete Shipping Address
                      </div>
                    )}
                    <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                      <div style={lineStyle}>{address.address || 'Address Line 1: Missing'}</div>
                      {address.address2 && <div style={lineStyle}>{address.address2}</div>}
                      <div style={lineStyle}>{address.city || 'City: Missing'}</div>
                      <div style={lineStyle}>{address.state || 'State: Missing'}</div>
                      <div style={lineStyle}>{INDIAN_PINCODE_RE.test(address.pincode || '') ? address.pincode : 'Pincode: Missing'}</div>
                      <div style={lineStyle}>{address.country || 'Country: Missing'}</div>
                      <div style={{ ...lineStyle, marginTop:6 }}>Phone: {address.phone || 'Missing'}</div>
                    </div>
                    {missing.length > 0 && (
                      <div style={{ marginTop:8, fontSize:12, color:'#b45309', fontWeight:700 }}>
                        {missing.map(field => field.label).join(', ')} required for shipment.
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="detail-item detail-full">
                <div className="detail-label">Update Status</div>
                {(() => {
                  const currentStatus = normalizeStatus(selOrder.orderStatus || selOrder.status) || 'pending';
                  const options = getOrderStatusOptions(currentStatus);
                  const locked = isOrderStatusLocked(currentStatus);
                  return (
                    <>
                      <div style={{ marginTop: 6 }}>
                        <CustomSelect
                          value={currentStatus}
                          disabled={locked}
                          onChange={val => handleStatusChange(selOrder._id, val)}
                          options={options.map(opt => ({ value: opt, label: ORDER_STATUS_LABELS[opt] }))}
                        />
                      </div>
                      {locked && (
                        <div style={{ fontSize: 11, color: '#FCA5A5', marginTop: 6 }}>
                          This order is in a final state - status can't be changed.
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
            <div className="items-box">
              <div className="items-title">Shipping - Shiprocket</div>
              {(() => {
                const status = selOrder.awbCode ? 'AWB Assigned' : (selOrder.shiprocketShipmentId ? (selOrder.shippingStatus || 'NEW').toUpperCase() : (selOrder.shippingStatus || 'Not Created'));
                const shippingCharge = selOrder.shippingCharge ?? selOrder.shipping_charges ?? selOrder.shiprocketShippingCharge;
                return (
                  <div style={{ border:'1px solid #e5e7eb', borderRadius:8, padding:12, marginBottom:12, background:'#f8fafc', display:'grid', gap:8 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
                      <span style={{ fontSize:11, letterSpacing:'.5px', textTransform:'uppercase', fontWeight:800, color:'#64748b' }}>Status</span>
                      <strong style={{ color:'#0f172a', fontSize:13 }}>{status}</strong>
                    </div>
                    {selOrder.shiprocketOrderId && <div style={{ display:'flex', justifyContent:'space-between', gap:12 }}><span style={{ fontSize:12, color:'#64748b' }}>Shiprocket Order ID</span><strong style={{ fontSize:12, color:'#0f172a' }}>{selOrder.shiprocketOrderId}</strong></div>}
                    {selOrder.shiprocketShipmentId && <div style={{ display:'flex', justifyContent:'space-between', gap:12 }}><span style={{ fontSize:12, color:'#64748b' }}>Shipment ID</span><strong style={{ fontSize:12, color:'#0f172a' }}>{selOrder.shiprocketShipmentId}</strong></div>}
                    <div style={{ display:'flex', justifyContent:'space-between', gap:12 }}><span style={{ fontSize:12, color:'#64748b' }}>Courier</span><strong style={{ fontSize:12, color:'#0f172a' }}>{selOrder.courierName || 'Not Assigned'}</strong></div>
                    <div style={{ display:'flex', justifyContent:'space-between', gap:12 }}><span style={{ fontSize:12, color:'#64748b' }}>AWB</span><strong style={{ fontSize:12, color:'#0f172a' }}>{selOrder.awbCode || 'Not Assigned'}</strong></div>
                    <div style={{ display:'flex', justifyContent:'space-between', gap:12 }}><span style={{ fontSize:12, color:'#64748b' }}>Shipping Charge</span><strong style={{ fontSize:12, color:'#d99a2b' }}>{selOrder.shiprocketShipmentId ? fmtINR(shippingCharge ?? 0) : (shippingCharge !== undefined && shippingCharge !== null ? fmtINR(shippingCharge) : 'Not Calculated')}</strong></div>
                  </div>
                );
              })()}
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {addressPrompt && addressPrompt.orderId === selOrder._id && (
                  <div style={{ border:'1px solid #fed7aa', background:'#fff7ed', borderRadius:8, padding:12, color:'#9a3412' }}>
                    <div style={{ fontWeight:800, fontSize:13, marginBottom:4 }}>Complete Shipping Address</div>
                    <div style={{ fontSize:12, marginBottom:8 }}>To create a Shiprocket shipment, please complete the delivery address.</div>
                    <div style={{ fontSize:12, fontWeight:700, marginBottom:6 }}>Missing:</div>
                    <ul style={{ margin:'0 0 10px 18px', padding:0, fontSize:12 }}>
                      {addressPrompt.missingFields.map((field, idx) => <li key={`missing-${field.key || field.label}-${idx}`}>{field.label}</li>)}
                    </ul>
                  </div>
                )}
                {!selOrder.shiprocketShipmentId && (
                  <button className="btn btn-primary" disabled={shipLoading} onClick={() => handleCreateShipment(selOrder)} style={{ alignSelf:'flex-start' }}>
                    {shipLoading ? 'Working...' : <><IconPackage /> Create Shipment</>}
                  </button>
                )}

                {selOrder.shiprocketShipmentId && !selOrder.awbCode && !courierOptions && (
                  <button className="btn btn-primary" disabled={shipLoading} onClick={() => handleGetCourierOptions(selOrder)} style={{ alignSelf:'flex-start' }}>
                    {shipLoading ? 'Loading couriers...' : <><IconTruck /> Choose Courier</>}
                  </button>
                )}

                {selOrder.shiprocketShipmentId && !selOrder.awbCode && courierOptions && (
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    <div style={{ fontSize:12, color:'#0f172a', fontWeight:700 }}>Select a courier:</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:200, overflowY:'auto' }}>
                      {courierOptions.map((c, idx) => (
                        <button
                          key={makeSafeKey('courier', { id: c.courierId || c.courierName || c.name }, idx)}
                          type="button"
                          onClick={() => setSelectedCourierId(c.courierId)}
                          style={{
                            display:'flex', justifyContent:'space-between', alignItems:'center',
                            padding:'10px 12px', borderRadius:10, textAlign:'left',
                            border: selectedCourierId === c.courierId ? '1.5px solid #d99a2b' : '1px solid #d8dee9',
                            background: selectedCourierId === c.courierId ? '#fff7e6' : '#ffffff',
                            color:'#0f172a', cursor:'pointer',
                          }}
                        >
                          <span>
                            <span style={{ fontWeight:700 }}>{c.courierName}</span>
                            <span style={{ display:'block', fontSize:11, color:'#64748b' }}>
                              {c.estimatedDays} days - Rating {c.rating}
                            </span>
                          </span>
                          <span style={{ fontWeight:800, color:'#d99a2b' }}>Rs. {Number(c.rate).toFixed(2)}</span>
                        </button>
                      ))}
                    </div>
                    <div style={{ display:'flex', gap:8 }}>
                      <button className="btn btn-ghost" onClick={() => { setCourierOptions(null); setSelectedCourierId(null); }}>
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary"
                        disabled={!selectedCourierId || shipLoading}
                        onClick={() => handleAssignAWB(selOrder)}
                      >
                        {shipLoading ? 'Shipping...' : <><IconTruck /> Ship Order</>}
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
                  {selOrder.shiprocketShipmentId && (
                    <button className="btn btn-ghost" disabled={shipLoading} onClick={() => handleGetLabel(selOrder)}><IconClipboard /> Get Label</button>
                  )}
                  {selOrder.shiprocketOrderId && (
                    <button className="btn btn-ghost" disabled={shipLoading} onClick={() => handleGetInvoice(selOrder)}><IconClipboard /> Get Invoice</button>
                  )}
                </div>
              </div>
              {shipMsg && <div className={'msg-box ' + (!shipMsg.startsWith('Error:') ? 'msg-s' : 'msg-e')} style={{ marginTop:12 }}>{shipMsg}</div>}
            </div>
            {(selOrder.products || selOrder.items || []).length > 0 && (
              <div className="items-box">
                <div className="items-title">Products ({(selOrder.products || selOrder.items || []).length})</div>
                {(selOrder.products || selOrder.items || []).map((item, i) => {
                  const prod  = item.product || item.productId || {};
                  const name  = prod.name  || item.name  || ('Product ' + (i + 1));
                  const price = prod.price || item.price || 0;
                  const qty   = item.quantity || 1;
                  const img   = getItemImage(item);
                  return (
                    <div className="item-row" key={makeSafeKey('item', item, i)}>
                      <ProductThumb src={img} name={name} size={64} />
                      <div className="item-info">
                        <div className="item-name">{name}</div>
                        {getItemVariant(item) && <div className="item-unit">Variant / Size: {getItemVariant(item)}</div>}
                        <div className="item-meta">
                          <span className="item-qty">Qty: {qty}</span>
                          <span className="item-unit">Unit: {fmtINR(price)}</span>
                          <span className="item-price">{fmtINR(price * qty)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {(() => {
                  const couponCode = getOrderCouponCode(selOrder);
                  const couponDiscount = getOrderCouponDiscount(selOrder);
                  const hasCoupon = Boolean(
                    couponCode || selOrder.coupon || selOrder.appliedCoupon || selOrder.couponDetails
                    || selOrder.couponDiscount !== undefined || selOrder.couponDiscountAmount !== undefined
                  );
                  if (!hasCoupon) {
                    return (
                      <div style={{ borderTop:'1px solid #e5e7eb', paddingTop:12, marginTop:4, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <span style={{ fontWeight:700, color:'#0f172a' }}>Order Total</span>
                        <span style={{ fontWeight:800, color:'#d99a2b', fontSize:15 }}>{fmtINR(selOrder.totalAmount || selOrder.total)}</span>
                      </div>
                    );
                  }
                  return (
                    <>
                      <div style={{ borderTop:'1px solid #e5e7eb', paddingTop:12, marginTop:4, display:'flex', flexDirection:'column', gap:8 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:16, fontSize:13 }}>
                          <span style={{ color:'#64748b' }}>Subtotal</span>
                          <span style={{ fontWeight:700, color:'#0f172a' }}>{fmtINR(getOrderSubtotal(selOrder))}</span>
                        </div>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:16, fontSize:13 }}>
                          <span style={{ color:'#64748b' }}>Coupon Used</span>
                          <span style={{ padding:'3px 8px', borderRadius:6, background:'#fff7e6', border:'1px solid rgba(217,154,43,.35)', color:'#b7791f', fontSize:11, fontWeight:800, letterSpacing:'.3px' }}>
                            {couponCode || 'Applied'}
                          </span>
                        </div>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:16, fontSize:13 }}>
                          <span style={{ color:'#64748b' }}>Coupon Discount</span>
                          <span style={{ color:'#059669', fontWeight:800 }}>-{fmtINR(couponDiscount)}</span>
                        </div>
                        {couponCode && (
                          <div style={{ fontSize:11, color:'#059669', fontWeight:700, marginTop:1 }}>
                            Coupon Applied: {couponCode} - You saved: {fmtINR(couponDiscount)}
                          </div>
                        )}
                      </div>
                      <div style={{ borderTop:'1px solid #e5e7eb', paddingTop:12, marginTop:4, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <span style={{ fontWeight:800, color:'#0f172a' }}>Final Order Total</span>
                        <span style={{ fontWeight:800, color:'#d99a2b', fontSize:15 }}>{fmtINR(selOrder.totalAmount || selOrder.total)}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
            {statusMsg && <div className={'msg-box ' + (!statusMsg.toLowerCase().includes('failed') ? 'msg-s' : 'msg-e')}>{statusMsg}</div>}
            <div className="modal-footer">
              <button className="btn btn-danger" onClick={() => { setSelOrder(null); openDelete(selOrder); }}><IconDelete /> Delete</button>
              <button className="btn btn-ghost"  onClick={() => setSelOrder(null)}>Close</button>
            </div>
          </div>
        </PortalModal>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <DeleteConfirmModal
          isOpen={true}
          title="Delete Order"
          message="Are you sure you want to delete this order?"
          itemName={deleteConfirm.label}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}

      {/* Delete Success Modal */}
      {deleteSuccess && (
        <DeleteSuccessModal
          isOpen={true}
          message="Deleted Successfully"
          subtitle="The order has been deleted successfully."
          onClose={handleDeleteSuccessClose}
        />
      )}

      {/* Edit Order Modal */}
      {editOrder && (
        <PortalModal onBackdropClick={() => !saving && setEditOrder(null)}>
          <div className="modal-box">
            <div className="modal-hdr">
              <span className="modal-title"><IconEdit /> Edit Order</span>
              <button className="modal-close" onClick={() => !saving && setEditOrder(null)}><IconClose /></button>
            </div>
            <div className="order-banner">
              <div className="order-banner-label">Order</div>
              <div className="order-banner-id">#{(editOrder._id || '').slice(-8).toUpperCase()}</div>
              <div className="order-banner-sub">{getCustomerName(editOrder)} - {fmtINR(editOrder.totalAmount || editOrder.total)}</div>
            </div>
            {(() => {
              const origOrderStatus   = normalizeStatus(editOrder.orderStatus || editOrder.status) || 'pending';
              const origPaymentStatus = getPaymentStatus(editOrder).toLowerCase() || 'pending';

              /* Options are computed off the ORIGINAL saved status (not the
                 in-progress edit), so the dropdown always represents valid
                 forward moves from where the order currently is. */
              const orderStatusOptions   = getOrderStatusOptions(origOrderStatus);
              const paymentStatusOptions = getPaymentStatusOptions(origPaymentStatus);

              const orderStatusLocked   = isOrderStatusLocked(origOrderStatus);
              const paymentStatusLocked = isPaymentStatusLocked(origPaymentStatus);

              return (
                <div className="status-row">
                  <div className="status-group">
                    <div className="status-group-label">Order Status</div>
                    <div className="select-with-icon">
                      <span className="select-icon" style={{ zIndex: 1 }}><IconClipboard /></span>
                      <CustomSelect
                        triggerClassName="ord-cs-styled-trigger with-icon"
                        value={editForm.orderStatus}
                        disabled={orderStatusLocked}
                        onChange={val => setEditForm(p => ({ ...p, orderStatus: val }))}
                        options={orderStatusOptions.map(opt => ({ value: opt, label: ORDER_STATUS_LABELS[opt] }))}
                      />
                    </div>
                    {orderStatusLocked && (
                      <div style={{ fontSize: 11, color: '#FCA5A5', marginTop: 6 }}>
                        {origOrderStatus === 'cancelled'
                          ? "Order is cancelled - status can't be changed."
                          : "Order is in a final state - status can't be changed."}
                      </div>
                    )}
                  </div>
                  <div className="status-group">
                    <div className="status-group-label">Payment Status</div>
                    <CustomSelect
                      triggerClassName="ord-cs-styled-trigger"
                      value={editForm.paymentStatus}
                      disabled={paymentStatusLocked}
                      onChange={val => setEditForm(p => ({ ...p, paymentStatus: val }))}
                        options={paymentStatusOptions.map(opt => ({ value: opt, label: PAYMENT_STATUS_LABELS[opt] || opt.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase()) }))}
                    />
                    {paymentStatusLocked && (
                      <div style={{ fontSize: 11, color: '#FCA5A5', marginTop: 6 }}>
                        Payment is {origPaymentStatus} - it can't be reverted or edited.
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
            <div className="edit-address-section">
              <div className="edit-section-title">Shipping Address</div>
              <div className="edit-address-grid">
                {[
                  ['name', 'Name', 'Enter full name'],
                  ['phone', 'Phone', 'Enter phone number'],
                  ['city', 'City', 'Enter city'],
                  ['state', 'State', 'Enter state'],
                  ['pincode', 'Pincode', 'Enter pincode'],
                ].map(([key, label, placeholder]) => (
                  <label className="edit-field" key={key}>
                    <span className="edit-label">{label}</span>
                    <input className="edit-input" value={editForm[key] || ''} onChange={e => setEditForm(p => ({ ...p, [key]: e.target.value }))} placeholder={placeholder} />
                  </label>
                ))}
                <label className="edit-field edit-field-wide">
                  <span className="edit-label">Address</span>
                  <textarea className="edit-input edit-textarea" maxLength={200} value={editForm.address || ''} onChange={e => setEditForm(p => ({ ...p, address: e.target.value }))} placeholder="Enter address..." />
                  <div className="char-hint">{(editForm.address || '').length}/200</div>
                </label>
                <label className="edit-field edit-field-wide">
                  <span className="edit-label">Address 2 <span className="edit-optional">(optional)</span></span>
                  <input className="edit-input" value={editForm.address2 || ''} onChange={e => setEditForm(p => ({ ...p, address2: e.target.value }))} placeholder="Apartment, landmark, etc." />
                </label>
              </div>
            </div>
            {editMsg && <div className={'msg-box ' + (!editMsg.toLowerCase().includes('failed') ? 'msg-s' : 'msg-e')}>{editMsg}</div>}
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setEditOrder(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleEditSave} disabled={saving} style={{ display:'flex', alignItems:'center', gap:6 }}>
                {saving
                  ? <><span style={{ width:14, height:14, border:'2px solid rgba(255,255,255,.4)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin .7s linear infinite', display:'inline-block' }} />Saving...</>
                  : <><IconSave /> Save Changes</>}
              </button>
            </div>
          </div>
        </PortalModal>
      )}
    </AdminLayout>
  );
};
export default Orders;







